import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectorRef,
  HostListener,
  ViewChild,
  ElementRef,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Location } from "@angular/common";
import { HttpClient, HttpEvent, HttpEventType } from "@angular/common/http";
import { ToastrService } from "ngx-toastr";
import { CdkDragDrop, moveItemInArray, transferArrayItem } from "@angular/cdk/drag-drop";
import { MatDialog } from "@angular/material/dialog";
import { environment } from "src/environments/environment";
import { FlowServiceService } from "src/app/Services/flow-service.service";
import { DataService } from "src/app/Services/dataservice.service";
import { UserserviceService } from "src/app/Services/userservice.service";
import { CaptionDialogComponent } from "../caption-dialog/caption-dialog.component";
import { ShowFlowImageDialogComponent } from "../show-flow-image-dialog/show-flow-image-dialog.component";
import { ItemDetailsDialogComponent } from "../item-details-dialog/item-details-dialog.component";
import { AddNewItemDialogComponent } from "../add-new-item-dialog/add-new-item-dialog.component";
import { MediaPreviewDialogComponent } from "./media-preview-dialog/media-preview-dialog.component";
import { MediaUploadService } from "src/app/Services/media-upload.service";
import {
  isCreatorPlanUser,
  isOrganizationAccountMode,
} from "src/app/shared/utils/plan.util";
import { openCreatorPlanUpgradeDialog } from "src/app/shared/utils/creator-plan-restrictions.util";
import { setCoinPurchaseReturnUrl } from "src/app/shared/utils/coin-purchase-return.util";
import {
  handleMaxPublishedFlowsError,
  isMaxPublishedFlowsError,
  handleFlowBundlePermissionError,
  isFlowBundlePermissionError,
  handleCreatorPublicOnlyError,
  isCreatorPublicOnlyError,
} from "src/app/shared/utils/guide-limit-error.util";
import { ItemLibraryService, ItemLibraryModel } from "src/app/Services/item-library.service";
import { MatAutocompleteSelectedEvent } from "@angular/material/autocomplete";
import { PiecesAiManualFallbackDialogComponent } from "./pieces-ai-manual-fallback-dialog.component";
import {
  debounceTime,
  distinctUntilChanged,
  switchMap,
  catchError,
  takeWhile,
  finalize,
} from "rxjs/operators";
import { interval, of, Subject, Subscription } from "rxjs";
import {
  FlowchestAiImportService,
  FlowchestAiImportDraft,
  FlowchestAiImportJobResponse,
} from "src/app/Services/flowchest-ai-import.service";
import {
  AiCoinBalance,
  AiCoinQuote,
  AiCoinService,
} from "src/app/Services/ai-coin.service";
import {
  CreateFlowSetupDialogComponent,
  CreateFlowSetupResult,
} from "../create-flow-setup-dialog/create-flow-setup-dialog.component";
import { FlowchestAiImportDialogComponent } from "../flowchest-ai-import-dialog/flowchest-ai-import-dialog.component";
import {
  LoadingModalComponent,
  LoadingModalData,
} from "../loading-modal/loading-modal.component";
import { FlowNavigationService } from "src/app/Services/flow-navigation.service";
import { OrgContextService } from "src/app/Services/org-context.service";
import { EnhancedRestrictedAccessComponent } from "../enhanced-restricted-access/enhanced-restricted-access.component";

interface QuickStep {
  id: string;
  title: string;
  description: string;
  imageUrl: string;
  posterUrl: string;
  fileName: string;
  file: File | null;
  /** Original file before browser compression — preferred for AI speech extraction. */
  originalFile?: File | null;
  isUploading: boolean;
  uploadProgress: number;
  showImage: boolean;
  showVideo: boolean;
  taggedItems?: any[]; // Tagged items for this step
  isSafetyCritical?: boolean; // Safety critical flag
  safetyInstructionsTitle?: string; // Safety instructions title
  safetyInstructionsDescription?: string; // Safety instructions description
  videoStartSec?: number;
  videoEndSec?: number;
  useSegmentPlayback?: boolean;
  /** When true (default for segment playback), caps preview clip to 30 seconds. */
  limitClipTo30Sec?: boolean;
}

interface PendingItemImage {
  id: string;
  imageUrl: string;
  fileName: string;
  isUploading: boolean;
  uploadProgress: number;
  itemName: string;
  itemQuantity: string;
  itemGroupName: string;
  itemWhereToFindIt: string;
}

export type CreateFlowMode = "full-video-ai" | "pieces-manual" | "pieces-ai";

@Component({
  selector: "app-quick-create-flow",
  templateUrl: "./quick-create-flow.component.html",
  styleUrls: ["./quick-create-flow.component.scss"],
})
export class QuickCreateFlowComponent implements OnInit, OnDestroy {
  @ViewChild("piecesAiConsentSection") piecesAiConsentSection?: ElementRef<HTMLElement>;

  createFlowMode: CreateFlowMode | null = null;
  setupComplete = false;
  piecesAiContentGenerated = false;
  piecesAiManualFallbackActive = false;
  piecesAiConfirmRights = false;
  piecesAiConfirmPolicy = false;
  piecesAiConsentHighlight = false;
  piecesAiNotes = "";
  piecesAiDocument: File | null = null;
  piecesAiDocumentName = "";
  piecesAiIncludeDocInSupporting = false;
  readonly maxReferenceDocumentMb = 10;

  /** Reference document from full-video AI import (for optional supporting docs). */
  private aiImportSourceDocument: File | null = null;
  private aiImportIncludeDocInSupporting = false;

  /** True when AI generated or assisted content is active. */
  isAiCreateMode = false;

  readonly aiPrivacyPolicyPoints = [
    "AI-generated titles, steps, and clips are drafts for your review before publishing.",
    "Imported video metadata and clips are processed on Flowchest servers and stored under your account.",
    "Publish only content you own or are authorized to republish.",
  ];
  showAiProcessingOverlay = false;
  aiProcessingMessage = "Preparing Flowchest AI...";
  aiProcessingPercent = 0;
  aiImportReady = false;
  aiCoinBalance: AiCoinBalance | null = null;
  fullVideoCoinQuote: AiCoinQuote | null = null;
  piecesAiCoinQuote: AiCoinQuote | null = null;

  private aiPollSub: Subscription | null = null;

  // Cover properties
  coverImageUrl: string = "";
  coverPosterUrl: string = "";
  coverFile: File | null = null;
  coverFileName: string = "";
  coverIsUploading: boolean = false;
  coverUploadProgress: number = 0;
  showCoverImage: boolean = false;
  showCoverVideo: boolean = false;

  // Persisted when a draft is saved (e.g. Flowchest AI auto-save)
  flowRowKey = "";
  flowUniqueId = "";

  // Flow properties
  title: string = "";
  description: string = "";
  duration: number = 0;
  durationUnit: string = 'Mins';
  CategoryName: string = "";
  isPaid: boolean = false;
  creatorRequiresPaidCreation = false;
  price: string = "";
  searchKeyWords: string = "";
  supportingDocumentDocuments: any[] = [];
  supportingDocumentHyperLinks: any[] = [];

  viewerFeatures = {
    showShare: true,
    showComments: true,
    showLike: true,
    showReview: true,
    showSave: true,
    showQr: true,
    showReport: true,
    showFollow: true,
    showSchedule: true,
    showConsult: true,
    showChatbot: true,
    showCopyLinks: true,
    showTransfer: true,
  };

  readonly viewerFeatureToggleDefs: Array<{ key: keyof QuickCreateFlowComponent['viewerFeatures']; label: string }> = [
    { key: 'showShare', label: 'Share / Embed' },
    { key: 'showComments', label: 'Comments' },
    { key: 'showLike', label: 'Like' },
    { key: 'showReview', label: 'Review / Feedback' },
    { key: 'showSave', label: 'Save / Bookmark' },
    { key: 'showQr', label: 'QR code' },
    { key: 'showReport', label: 'Report' },
    { key: 'showFollow', label: 'Follow creator' },
    { key: 'showSchedule', label: 'Schedule reminder' },
    { key: 'showConsult', label: 'Consult' },
    { key: 'showChatbot', label: 'Flow Assist chatbot' },
    { key: 'showCopyLinks', label: 'Copy Cover / Step / Items links' },
    { key: 'showTransfer', label: 'Transfer ownership' },
  ];
  
  // Cover safety properties
  coverIsSafetyCritical: boolean = false;
  coverSafetyInstructionsTitle: string = "";
  coverSafetyInstructionsDescription: string = "";

  // Steps
  quickSteps: QuickStep[] = [];
  selectedStepIndex: number = -1;
  
  // Step item tagging properties
  currentStepTaggedItems: any[] = [];
  stepItemSearch: string = "";
  stepItemSuggestions: any[] = [];
  stepItemInput$ = new Subject<string>();

  // Items
  inames: any[] = [];
  isItemsRequired: boolean = false;
  itemGroupName: string = "";
  itemName: string = "";
  itemQuantity: string = "";
  itemImageUrl: string = "";
  itemWhereToFindIt: string = "";
  itemGroupList: string[] = [];
  showItemImage: boolean = false;
  itemUploadProgress: number = 0;
  groupedData: Record<string, any[]> = {}; // Grouped items data
  expandedGroups: { [key: string]: boolean } = {}; // Store expanded state for item groups
  pendingItemImages: PendingItemImage[] = []; // Uploaded images waiting for details
  
  // Library integration
  libraryGroupSuggestions: ItemLibraryModel[] = [];
  libraryItemSuggestions: ItemLibraryModel[] = [];
  allLibraryItems: ItemLibraryModel[] = []; // Store all library items
  groupNameInput$ = new Subject<string>();
  itemNameInput$ = new Subject<string>();
  selectedLibraryItem: ItemLibraryModel | null = null;

  // FAQs
  _allFaqs: any[] = [];
  faqGroupName: string = "";
  faqQuestion: string = "";
  faqAnswer: string = "";
  faqGroupList: string[] = [];

  // UI state
  isMobileView: boolean = false;
  maxtitlelenght = 80;
  maxdesclenght = 500;

  // Tabs
  activeTab: 'upload' | 'review' = 'upload';
  tabs = [
    { id: 'upload', label: 'Upload & Details', icon: 'cloud_upload' },
    { id: 'review', label: 'Review & Summary', icon: 'preview' }
  ];

  // Summary/Review state
  selectedSummaryIndex: number = -2; // -2 = Cover, -1 = Items, >= 0 = Steps
  showDescriptionExpanded: boolean = false; // Control description expand/collapse
  showAiPolicyExpanded = false; // Collapsed by default after AI extraction (desktop + mobile)
  showPiecesAiBannerExpanded = false;
  private segmentPlaybackCleanup: (() => void) | null = null;

  // Utility method for truncating text
  truncateText(text: string, maxLength: number): string {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }

  userData: any;
  returnUrl: string | null = null;

  // Loading modal
  loadingModalRef: any = null;
  isMainUploadLoaderActive = false;
  private uploadSessionActive = false;
  private uploadSessionTotal = 0;

  constructor(
    protected router: Router,
    private location: Location,
    private activatedRoute: ActivatedRoute,
    private http: HttpClient,
    protected toastr: ToastrService,
    protected cdr: ChangeDetectorRef,
    private itemLibraryService: ItemLibraryService,
    private flowServiceService: FlowServiceService,
    private dataService: DataService,
    private userserviceService: UserserviceService,
    protected dialog: MatDialog,
    private mediaUploadService: MediaUploadService,
    protected flowchestAiImportService: FlowchestAiImportService,
    private flowNavigationService: FlowNavigationService,
    private aiCoinService: AiCoinService,
    private orgContext: OrgContextService
  ) {}

  /** Org content goes through Submit for approval; authors cannot publish directly. */
  get requiresOrgApproval(): boolean {
    return !!this.orgContext.contentScope().organizationId;
  }

  get showMainCreateUi(): boolean {
    if (!this.setupComplete) return false;
    if (this.showAiProcessingOverlay) return false;
    if (this.createFlowMode === "full-video-ai" && !this.aiImportReady) return false;
    return true;
  }

  get createFlowHeaderTitle(): string {
    return "SMART CREATE GUIDE";
  }

  get createFlowHeaderSubtitle(): string {
    switch (this.createFlowMode) {
      case "full-video-ai":
        return "Review AI-generated content and publish when ready";
      case "pieces-ai":
        if (this.piecesAiManualFallbackActive) {
          return "AI text wasn’t generated — add titles and descriptions for each step yourself";
        }
        return this.piecesAiContentGenerated
          ? "Review AI-written step content and publish when ready"
          : "Upload one image or video per step, then generate content with AI";
      case "pieces-manual":
        return "Upload your step images or videos and add titles and descriptions";
      default:
        return "Upload files and add details in one screen";
    }
  }

  /** True when AI content is ready for the user to review before publishing. */
  get isAiReviewPhase(): boolean {
    if (!this.isAiCreateMode) return false;
    if (this.createFlowMode === "full-video-ai") return this.aiImportReady;
    if (this.createFlowMode === "pieces-ai") return this.piecesAiContentGenerated;
    return false;
  }

  private getStackedDialogConfig(width: string, extra: Record<string, unknown> = {}) {
    const panelClass = [
      "quick-create-stacked-dialog",
      ...(Array.isArray(extra.panelClass)
        ? extra.panelClass
        : extra.panelClass
          ? [extra.panelClass]
          : []),
    ];
    return {
      width,
      maxWidth: "95vw",
      maxHeight: "90dvh",
      hasBackdrop: true,
      // Prevent click-through from a previous dialog's button from closing this one.
      disableClose: true,
      autoFocus: "first-tabbable",
      backdropClass: "quick-create-stacked-dialog-backdrop",
      ...extra,
      panelClass,
    };
  }

  isCoverMediaVideo(): boolean {
    if (!this.coverImageUrl) return false;
    return this.showCoverVideo || this.isVideoUrl(this.coverImageUrl);
  }

  isCoverMediaImage(): boolean {
    if (!this.coverImageUrl) return false;
    if (this.isCoverMediaVideo()) return false;
    return this.showCoverImage || this.isImageUrl(this.coverImageUrl);
  }

  isStepMediaVideo(step: QuickStep | undefined): boolean {
    if (!step?.imageUrl) return false;
    return step.showVideo || this.isVideoUrl(step.imageUrl);
  }

  isStepMediaImage(step: QuickStep | undefined): boolean {
    if (!step?.imageUrl) return false;
    if (this.isStepMediaVideo(step)) return false;
    return step.showImage || this.isImageUrl(step.imageUrl);
  }

  private syncStepMediaFlags(step: QuickStep): void {
    if (!step.imageUrl) {
      step.showImage = false;
      step.showVideo = false;
      return;
    }
    if (this.isVideoUrl(step.imageUrl)) {
      step.showVideo = true;
      step.showImage = false;
      return;
    }
    if (this.isImageUrl(step.imageUrl)) {
      step.showImage = true;
      step.showVideo = false;
    }
  }

  private syncAllStepMediaFlags(): void {
    this.quickSteps.forEach((step) => this.syncStepMediaFlags(step));
    if (this.coverImageUrl) {
      this.isCoverImageOrVideo(this.coverImageUrl);
    }
  }

  goToUploadTabForMedia(): void {
    this.activeTab = "upload";
    this.cdr.detectChanges();
    setTimeout(() => {
      document.querySelector(".cover-section")?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }, 50);
  }

  get uploadSectionTitle(): string {
    if (this.createFlowMode === "pieces-ai" || this.createFlowMode === "pieces-manual") {
      return "Upload step media";
    }
    return "Upload all files";
  }

  get uploadSectionHint(): string {
    if (this.createFlowMode === "pieces-ai" || this.createFlowMode === "pieces-manual") {
      return "Select images or videos at once. The first file becomes the cover; each remaining file becomes a step.";
    }
    return "Select all files at once. The first file will be set as cover, and the rest will be added as steps.";
  }

  get canGeneratePiecesAi(): boolean {
    if (this.createFlowMode !== "pieces-ai" || this.piecesAiContentGenerated) return false;
    if (this.piecesAiReadyCount <= 0) return false;
    if (!this.piecesAiCoinQuote) return true;
    return this.piecesAiCoinQuote.sufficient;
  }

  get hasPiecesAiConsent(): boolean {
    return this.piecesAiConfirmRights && this.piecesAiConfirmPolicy;
  }

  get piecesAiReadyCount(): number {
    return this.quickSteps.filter((s) => this.isPiecesStepReady(s)).length;
  }

  /** Image or video media counts as a ready step. */
  private isPiecesStepReady(step: QuickStep): boolean {
    if (!step?.imageUrl || step.isUploading) {
      return false;
    }
    return (
      this.isVideoUrl(step.imageUrl) ||
      this.isImageUrl(step.imageUrl) ||
      !!step.showVideo ||
      !!step.showImage
    );
  }

  get aiCoinBalanceLabel(): string {
    if (!this.aiCoinBalance) return "AI coins loading...";
    return `${this.aiCoinBalance.availablePlanCoins} plan + ${this.aiCoinBalance.availableTopUpCoins} top-up = ${this.aiCoinBalance.totalAvailable} coins`;
  }

  get fullVideoCoinQuoteLabel(): string {
    if (!this.fullVideoCoinQuote) return "";
    return `${this.fullVideoCoinQuote.required} coins (${this.fullVideoCoinQuote.breakdown})`;
  }

  get piecesAiCoinQuoteLabel(): string {
    if (!this.piecesAiCoinQuote) return "";
    return `${this.piecesAiCoinQuote.required} coins for ${this.piecesAiReadyCount} step${this.piecesAiReadyCount === 1 ? "" : "s"}`;
  }

  get durationDisplay(): string {
    const val = this.duration || 0;
    if (val === 0) return '';
    const unit = this.durationUnit || 'Mins';
    return `${val} ${val === 1 ? unit.replace(/s$/i, '') : unit}`;
  }

  ngOnInit() {
    document.body.style.overflow = "hidden";
    this.checkMobileView();
    this.returnUrl =
      this.activatedRoute.snapshot.queryParams["returnUrl"] ?? null;
    this.userData = JSON.parse(localStorage.getItem("LoggedInUser"));
    this.applyCreatorPaidOnlyRules();
    this.CategoryName = localStorage.getItem("selectedCategoryName") || "";
    this.loadAiCoinBalance();
    this.setupLibrarySearch();
    this.setupStepItemSearch();
    this.openCreateFlowSetup();
  }

  ngOnDestroy() {
    this.aiPollSub?.unsubscribe();
    this.clearSegmentPlayback();
    document.body.style.overflow = "";
  }

  private openCreateFlowSetup(): void {
    const ref = this.dialog.open(CreateFlowSetupDialogComponent, {
      panelClass: ["dialoguebox", "create-flow-setup-dialog-pane"],
      disableClose: true,
      width: "680px",
      maxWidth: "95vw",
    });

    ref.afterClosed().subscribe((result: CreateFlowSetupResult | undefined) => {
      if (!result || result.cancelled !== false) {
        this.navigateBackFromFlow();
        return;
      }

      const mode = result.mode;
      this.createFlowMode = mode;
      if (mode === "full-video-ai") {
        this.isAiCreateMode = true;
        this.openFlowchestAiImportDialog();
        return;
      }

      this.setupComplete = true;
      this.isAiCreateMode = mode === "pieces-ai";
      this.loadExistingData();
      this.cdr.detectChanges();
    });
  }

  private openFlowchestAiImportDialog(): void {
    const ref = this.dialog.open(FlowchestAiImportDialogComponent, {
      panelClass: ["dialoguebox", "flowchest-ai-import-dialog-pane"],
      disableClose: true,
      width: "520px",
      maxWidth: "95vw",
      autoFocus: "#videoFile",
    });

    ref.afterClosed().subscribe((result) => {
      if (!result?.videoFile) {
        this.navigateBackFromFlow();
        return;
      }

      this.setupComplete = true;
      void this.refreshFullVideoQuote(result.videoDurationSec);
      this.aiImportSourceDocument = result.sourceDocument ?? null;
      this.aiImportIncludeDocInSupporting = !!result.includeDocumentInSupportingDocs;
      this.startFlowchestAiImportUpload(
        result.videoFile,
        result.confirmRights,
        result.sourceTitle,
        result.sourceNotes,
        result.videoDurationSec,
        result.sourceDocument,
        result.limitStepsTo30Sec ? 30 : undefined
      );
    });
  }

  protected async startFlowchestAiImportUpload(
    file: File,
    confirmRights: boolean,
    sourceTitle?: string,
    sourceNotes?: string,
    videoDurationSec?: number,
    sourceDocument?: File | null,
    maxClipSec?: number
  ): Promise<void> {
    const quote = await this.safeGetQuote("full-video", videoDurationSec, undefined);
    if (quote && !quote.sufficient) {
      this.toastr.warning(
        `You need ${quote.required} coins but only have ${quote.available}. Top up to use Smart Create AI.`,
        "Insufficient AI coins",
        { timeOut: 9000 }
      );
      this.navigateToCoinTopUp();
      return;
    }

    this.showAiProcessingOverlay = true;
    this.aiImportReady = false;
    this.aiProcessingMessage = "Optimizing video for upload...";
    this.aiProcessingPercent = 0;
    this.cdr.detectChanges();

    let uploadFile = file;
    try {
      uploadFile = await this.mediaUploadService.prepareFileForUpload(file, (progress) => {
        this.aiProcessingPercent = Math.min(25, Math.round(progress * 0.25));
        this.cdr.detectChanges();
      });
    } catch (error: any) {
      this.handleAiImportError(error?.message || "Could not optimize video for upload.");
      return;
    }

    this.aiProcessingMessage = "Uploading video and starting Flowchest AI...";
    this.cdr.detectChanges();

    this.flowchestAiImportService
      .startUpload(
        uploadFile,
        confirmRights,
        sourceTitle,
        sourceNotes,
        videoDurationSec,
        sourceDocument,
        undefined,
        maxClipSec
      )
      .subscribe({
        next: (res) => {
          this.fullVideoCoinQuote = null;
          if (!res?.jobId) {
            this.handleAiImportError("Could not start Flowchest AI import from upload.");
            return;
          }
          this.pollAiImportJob(res.jobId);
        },
        error: (err) => {
          if (err?.status === 402) {
            this.loadAiCoinBalance();
          }
          this.handleAiImportError(
            this.formatAiHttpError(err, "Failed to upload and start Flowchest AI import.")
          );
        },
      });
  }

  private pollAiImportJob(
    jobId: string,
    onCompleted?: (draft: FlowchestAiImportDraft) => void,
    onFailed?: (message: string) => void
  ): void {
    this.aiPollSub?.unsubscribe();
    this.aiPollSub = interval(2500)
      .pipe(
        switchMap(() => this.flowchestAiImportService.getJob(jobId)),
        takeWhile(
          (job) => job?.status !== "Completed" && job?.status !== "Failed",
          true
        ),
        finalize(() => this.cdr.detectChanges())
      )
      .subscribe({
        next: (job) => {
          if (!job) return;
          this.aiProcessingPercent = job.progressPercent ?? 0;
          this.aiProcessingMessage = job.statusMessage || job.status || "Processing...";
          this.cdr.detectChanges();

          if (job.status === "Failed") {
            const message = job.errorMessage || "Flowchest AI import failed.";
            if (onFailed) {
              onFailed(message);
            } else {
              this.handleAiImportError(message);
            }
            return;
          }

          if (job.status === "Completed" && job.result) {
            this.aiPollSub?.unsubscribe();
            if (onCompleted) {
              onCompleted(job.result);
            } else {
              this.applyFullVideoAiDraft(job.result);
            }
          }
        },
        error: (err) => {
          const msg = err?.error?.error || err?.message || "Failed to check import status.";
          if (onFailed) {
            onFailed(msg);
          } else {
            this.handleAiImportError(msg);
          }
        },
      });
  }

  protected applyFullVideoAiDraft(draft: FlowchestAiImportDraft): void {
    this.title = draft.title || "";
    this.description = draft.description || "";
    this.duration = draft.duration || 0;
    this.durationUnit = draft.durationUnit || "Mins";
    this.searchKeyWords = draft.searchKeyWords || "";
    const segmentPlayback = !!draft.useSegmentPlayback;

    const importedItems = (draft.components || [])
      .map((item) => ({
        itemkey: item.itemkey || "",
        itemValue: item.itemValue || "",
        url: item.url || "",
        wheretofindit: item.wheretofindit || "",
        groupName: item.groupName || "",
      }))
      .filter((item) => item.itemkey.length > 0);

    this.inames = importedItems;
    this.isItemsRequired = importedItems.length > 0;
    if (this.isItemsRequired) {
      localStorage.setItem("ItemsRequired", JSON.stringify(this.inames));
      this.groupDataByGroupName();
      this.getAllItemGroupName();
    }

    let rawCover = draft.coverUrl || "";
    let rawPoster = draft.coverPosterUrl || "";
    const firstStepMedia = draft.steps?.[0];
    if (!rawCover && firstStepMedia) {
      rawCover = firstStepMedia.videoUrl || firstStepMedia.posterUrl || "";
      rawPoster = firstStepMedia.posterUrl || rawPoster;
    }
    this.coverImageUrl = rawCover;
    this.coverPosterUrl = rawPoster || (this.isImageUrl(rawCover) ? rawCover : "");
    if (this.coverImageUrl) {
      this.isCoverImageOrVideo(this.coverImageUrl);
    }

    this.quickSteps = (draft.steps || []).map((step, index) => {
      const mediaUrl = step.videoUrl || step.posterUrl || "";
      return {
        id: `flowchest-ai-step-${index}`,
        title: step.title || "",
        description: step.description || "",
        imageUrl: mediaUrl,
        posterUrl: step.posterUrl || "",
        fileName: "",
        file: null,
        isUploading: false,
        uploadProgress: 100,
        showImage: mediaUrl ? this.isImageUrl(mediaUrl) : false,
        showVideo: mediaUrl ? this.isVideoUrl(mediaUrl) : false,
        videoStartSec: segmentPlayback ? step.startSec : undefined,
        videoEndSec: segmentPlayback ? step.endSec : undefined,
        useSegmentPlayback: segmentPlayback,
        limitClipTo30Sec: segmentPlayback,
        taggedItems: [],
        isSafetyCritical: false,
        safetyInstructionsTitle: "",
        safetyInstructionsDescription: "",
      };
    });

    this.showAiProcessingOverlay = false;
    this.aiImportReady = true;
    this.showAiPolicyExpanded = false;
    this.activeTab = "review";
    if (this.quickSteps.length > 0) {
      this.selectedSummaryIndex = 0;
    }

    if (this.aiImportIncludeDocInSupporting && this.aiImportSourceDocument) {
      this.addReferenceDocumentToSupportingDocs(this.aiImportSourceDocument);
    }
    this.aiImportSourceDocument = null;
    this.aiImportIncludeDocInSupporting = false;

    if (draft.warningMessage && draft.useSegmentPlayback && (draft.stepClipsGenerated ?? 0) > 0) {
      this.toastr.info(draft.warningMessage, "Flowchest AI", { timeOut: 12000 });
    } else if (draft.warningMessage) {
      this.toastr.warning(draft.warningMessage, "Flowchest AI", { timeOut: 12000 });
    } else if ((draft.stepClipsGenerated ?? 0) > 0) {
      this.toastr.success(
        `Flowchest AI created ${draft.stepClipsGenerated} step video(s). Review before publishing.`
      );
    } else {
      this.toastr.success(
        "Flowchest AI import complete. Review your guide and edit before publishing."
      );
    }

    this.autoSaveDraftSilently();
    setTimeout(() => this.refreshActiveSegmentVideo(), 100);
    this.cdr.detectChanges();
  }

  async generatePiecesAiContent(): Promise<void> {
    if (this.createFlowMode !== "pieces-ai" || this.piecesAiContentGenerated) return;

    if (this.piecesAiReadyCount === 0) {
      this.toastr.warning(
        "Upload at least one step image or video before generating with AI.",
        "Flowchest AI",
        { timeOut: 6000 }
      );
      return;
    }

    if (!this.hasPiecesAiConsent) {
      this.remindPiecesAiConsent();
      return;
    }

    const readySteps = this.quickSteps.filter((s) => this.isPiecesStepReady(s));

    const quote = await this.safeGetQuote("pieces-ai", undefined, readySteps.length);
    this.piecesAiCoinQuote = quote;
    if (quote && !quote.sufficient) {
      this.offerPiecesAiManualFallback(
        `You need ${quote.required} coins but only have ${quote.available}. You can buy coins, or add titles and descriptions yourself.`,
        true
      );
      return;
    }

    this.showAiProcessingOverlay = true;
    this.aiProcessingMessage = "AI is analyzing your videos and writing content...";
    this.aiProcessingPercent = 10;
    this.cdr.detectChanges();

    void this.ensureStepFilesForAi(readySteps).then(() => {
      this.flowchestAiImportService
      .generateFromSteps({
        confirmRights: true,
        sourceTitle: (this.title || "").trim() || undefined,
        sourceNotes: (this.piecesAiNotes || "").trim() || undefined,
        coverUrl: this.coverImageUrl || undefined,
        coverPosterUrl: this.coverPosterUrl || undefined,
        sourceDocument: this.piecesAiDocument,
        steps: readySteps.map((step) => ({
          stepIndex: this.quickSteps.indexOf(step) + 1,
          videoUrl: step.imageUrl,
          posterUrl: step.posterUrl || undefined,
          title: step.title || undefined,
          description: step.description || undefined,
          mediaType: this.isImageUrl(step.imageUrl) || step.showImage ? "image" : "video",
          file: step.originalFile || step.file || undefined,
        })),
      })
      .subscribe({
        next: (res) => {
          this.piecesAiCoinQuote = null;
          const jobResponse = res as FlowchestAiImportJobResponse;
          if (jobResponse?.jobId) {
            this.pollAiImportJob(
              jobResponse.jobId,
              (draft) => {
                this.applyPiecesAiDraft(draft, readySteps);
                if (this.piecesAiIncludeDocInSupporting && this.piecesAiDocument) {
                  this.addReferenceDocumentToSupportingDocs(this.piecesAiDocument);
                }
                this.showAiProcessingOverlay = false;
                this.piecesAiContentGenerated = true;
                this.piecesAiManualFallbackActive = false;
                this.aiImportReady = true;
                this.showPiecesAiResultToasts(draft);
                this.autoSaveDraftSilently();
                this.cdr.detectChanges();
              },
              (message) => {
                this.showAiProcessingOverlay = false;
                this.offerPiecesAiManualFallback(
                  message ||
                    "AI could not generate step text from your videos."
                );
                this.cdr.detectChanges();
              }
            );
            return;
          }

          const draft = res as FlowchestAiImportDraft;
          this.applyPiecesAiDraft(draft, readySteps);
          if (this.piecesAiIncludeDocInSupporting && this.piecesAiDocument) {
            this.addReferenceDocumentToSupportingDocs(this.piecesAiDocument);
          }
          this.showAiProcessingOverlay = false;
          this.piecesAiContentGenerated = true;
          this.piecesAiManualFallbackActive = false;
          this.aiImportReady = true;
          this.showPiecesAiResultToasts(draft);
          this.autoSaveDraftSilently();
          this.cdr.detectChanges();
        },
        error: (err) => {
          this.showAiProcessingOverlay = false;
          if (err?.status === 402) {
            this.loadAiCoinBalance();
            this.offerPiecesAiManualFallback(
              "Not enough AI coins to generate content. You can buy coins, or add titles and descriptions yourself.",
              true
            );
            return;
          }
          const errorMsg = this.formatAiHttpError(
            err,
            "Failed to generate content from step videos."
          );
          this.offerPiecesAiManualFallback(errorMsg);
          this.cdr.detectChanges();
        },
      });
    });
  }

  /** Ask the user to write step text manually when AI generation cannot complete. */
  private offerPiecesAiManualFallback(
    reason: string,
    offerBuyCoins = false
  ): void {
    const dialogRef = this.dialog.open(PiecesAiManualFallbackDialogComponent, {
      ...this.getStackedDialogConfig("460px", {
        panelClass: "pieces-ai-manual-fallback-pane",
        disableClose: true,
        autoFocus: false,
      }),
      data: {
        reason,
        offerBuyCoins,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.action === "buy-coins") {
        this.navigateToCoinTopUp();
        return;
      }
      if (result?.action !== "manual") {
        return;
      }

      this.enablePiecesAiManualFallback();
    });
  }

  private enablePiecesAiManualFallback(): void {
    this.piecesAiManualFallbackActive = true;
    this.showAiProcessingOverlay = false;
    this.toastr.info(
      "Add a title and description for each step. Click a step to edit.",
      "Add details yourself",
      { timeOut: 9000 }
    );

    // Wait until the previous dialog's mouseup/click settles so the caption dialog
    // is not immediately dismissed by click-through.
    setTimeout(() => this.openFirstEmptyStepForManualEdit(), 0);
    this.cdr.detectChanges();
  }

  openFirstEmptyStepForManualEdit(): void {
    const firstEmptyIndex = this.quickSteps.findIndex(
      (step) => !(step.title || "").trim() || !(step.description || "").trim()
    );
    const index = firstEmptyIndex >= 0 ? firstEmptyIndex : 0;
    if (this.quickSteps.length > 0) {
      this.openStepCaptionDialog(index);
    }
  }

  private async ensureStepFilesForAi(steps: QuickStep[]): Promise<void> {
    await Promise.all(
      steps.map(async (step) => {
        if (step.file || !step.imageUrl) {
          return;
        }

        try {
          const response = await fetch(step.imageUrl);
          if (!response.ok) {
            return;
          }

          const blob = await response.blob();
          if (!blob.size) {
            return;
          }

          const fallbackName = step.fileName || `step-${this.quickSteps.indexOf(step) + 1}.mp4`;
          step.file = new File([blob], fallbackName, {
            type: blob.type || "video/mp4",
          });
          if (!step.fileName) {
            step.fileName = fallbackName;
          }
        } catch {
          // Server will attempt authenticated blob download using videoUrl.
        }
      })
    );
  }

  onPiecesAiConsentChange(): void {
    if (this.hasPiecesAiConsent) {
      this.piecesAiConsentHighlight = false;
    }
  }

  private loadAiCoinBalance(): void {
    this.aiCoinService.getBalance().subscribe({
      next: (balance) => {
        this.aiCoinBalance = balance;
        this.refreshPiecesAiQuote();
        this.cdr.detectChanges();
      },
      error: () => {
        this.aiCoinBalance = null;
      },
    });
  }

  private async refreshFullVideoQuote(durationSec?: number): Promise<void> {
    if (!durationSec || durationSec <= 0) {
      this.fullVideoCoinQuote = null;
      return;
    }
    const quote = await this.safeGetQuote("full-video", durationSec, undefined);
    this.fullVideoCoinQuote = quote;
    this.cdr.detectChanges();
  }

  private refreshPiecesAiQuote(): void {
    if (this.createFlowMode !== "pieces-ai" || this.piecesAiReadyCount <= 0) {
      this.piecesAiCoinQuote = null;
      return;
    }
    this.aiCoinService.getQuote("pieces-ai", undefined, this.piecesAiReadyCount).subscribe({
      next: (quote) => {
        this.piecesAiCoinQuote = quote;
        this.cdr.detectChanges();
      },
      error: () => {
        this.piecesAiCoinQuote = null;
      },
    });
  }

  private async safeGetQuote(mode: string, durationSec?: number, stepCount?: number): Promise<AiCoinQuote | null> {
    return await new Promise((resolve) => {
      this.aiCoinService.getQuote(mode, durationSec, stepCount).subscribe({
        next: (quote) => {
          this.loadAiCoinBalance();
          resolve(quote);
        },
        error: () => resolve(null),
      });
    });
  }

  private scrollToPiecesAiConsent(): void {
    this.showPiecesAiBannerExpanded = true;
    this.cdr.detectChanges();
    setTimeout(() => {
      this.piecesAiConsentSection?.nativeElement?.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    });
  }

  private remindPiecesAiConsent(): void {
    this.piecesAiConsentHighlight = true;
    this.scrollToPiecesAiConsent();

    if (!this.piecesAiConfirmRights && !this.piecesAiConfirmPolicy) {
      this.toastr.warning(
        "Please check both consent boxes before generating with AI.",
        "Consent required",
        { timeOut: 6000 }
      );
      return;
    }
    if (!this.piecesAiConfirmRights) {
      this.toastr.warning(
        "Please confirm you have permission to use and republish clips from these videos.",
        "Consent required",
        { timeOut: 6000 }
      );
      return;
    }
    this.toastr.warning(
      "Please agree to the Privacy Policy and Terms & Conditions.",
      "Consent required",
      { timeOut: 6000 }
    );
  }

  private showPiecesAiResultToasts(draft: FlowchestAiImportDraft): void {
    if (draft.warningMessage) {
      this.toastr.warning(draft.warningMessage, "Flowchest AI", { timeOut: 12000 });
    } else if ((draft.transcriptionSuccessCount ?? 0) > 0) {
      this.toastr.success(
        `AI generated content from ${draft.transcriptionSuccessCount} step video transcript(s). Review before publishing.`,
        "Flowchest AI"
      );
    } else {
      this.toastr.success(
        "AI generated flow details and step content based on your videos. Review before publishing.",
        "Flowchest AI"
      );
    }
  }

  private applyPiecesAiDraft(draft: FlowchestAiImportDraft, readySteps: QuickStep[]): void {
    if (draft.title?.trim()) this.title = draft.title.trim();
    if (draft.description?.trim()) this.description = draft.description.trim();
    if (draft.duration) this.duration = draft.duration;
    if (draft.durationUnit) this.durationUnit = draft.durationUnit;
    if (draft.searchKeyWords?.trim()) this.searchKeyWords = draft.searchKeyWords.trim();

    const importedItems = (draft.components || [])
      .map((item) => ({
        itemkey: item.itemkey || "",
        itemValue: item.itemValue || "",
        url: item.url || "",
        wheretofindit: item.wheretofindit || "",
        groupName: item.groupName || "",
      }))
      .filter((item) => item.itemkey.length > 0);

    if (importedItems.length > 0 && this.inames.length === 0) {
      this.inames = importedItems;
      this.isItemsRequired = true;
      localStorage.setItem("ItemsRequired", JSON.stringify(this.inames));
      this.groupDataByGroupName();
      this.getAllItemGroupName();
    }

    const stepByIndex = new Map<number, QuickStep>();
    readySteps.forEach((step) => {
      const idx = this.quickSteps.indexOf(step) + 1;
      if (idx > 0) {
        stepByIndex.set(idx, step);
      }
    });

    draft.steps?.forEach((aiStep, index) => {
      const stepIndex = aiStep.stepIndex > 0 ? aiStep.stepIndex : index + 1;
      const localStep = stepByIndex.get(stepIndex) ?? readySteps[index];
      if (!localStep) return;
      const quickIndex = this.quickSteps.indexOf(localStep);
      if (quickIndex < 0) return;
      if (aiStep.title?.trim()) {
        this.quickSteps[quickIndex].title = aiStep.title.trim();
      }
      if (aiStep.description?.trim()) {
        this.quickSteps[quickIndex].description = aiStep.description.trim();
      }
    });

    this.syncAllStepMediaFlags();
    this.showPiecesAiBannerExpanded = false;
    this.showAiPolicyExpanded = false;
    this.activeTab = "review";

    if (this.quickSteps.length > 0) {
      this.selectedSummaryIndex = 0;
    }
  }

  protected formatAiHttpError(err: any, fallback: string): string {
    const apiMsg = err?.error?.error ?? err?.error?.message;
    if (apiMsg) {
      return typeof apiMsg === "string" ? apiMsg : fallback;
    }
    if (err?.status === 401) return "Session expired. Please sign in again.";
    return err?.message || fallback;
  }

  protected handleAiImportError(message: string): void {
    this.showAiProcessingOverlay = false;
    this.aiImportSourceDocument = null;
    this.aiImportIncludeDocInSupporting = false;
    this.toastr.error(message, "Flowchest AI", { timeOut: 10000 });
    this.navigateBackFromFlow();
  }

  onPiecesAiDocumentSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.piecesAiDocument = file;
    this.piecesAiDocumentName = file?.name ?? "";
    this.piecesAiIncludeDocInSupporting = false;

    if (file && !this.isSupportedReferenceDocument(file)) {
      this.piecesAiDocument = null;
      this.piecesAiDocumentName = "";
      if (input) {
        input.value = "";
      }
      this.toastr.error(
        "Unsupported document format. Upload .docx, .pptx, or .pdf.",
        "Reference document"
      );
      return;
    }

    if (file && file.size > this.maxReferenceDocumentMb * 1024 * 1024) {
      this.piecesAiDocument = null;
      this.piecesAiDocumentName = "";
      if (input) {
        input.value = "";
      }
      this.toastr.error(
        `Reference document is too large. Maximum size is ${this.maxReferenceDocumentMb} MB.`,
        "Reference document"
      );
    }
  }

  clearPiecesAiDocument(): void {
    this.piecesAiDocument = null;
    this.piecesAiDocumentName = "";
    this.piecesAiIncludeDocInSupporting = false;
  }

  private isSupportedReferenceDocument(file: File): boolean {
    const ext = (file.name.split(".").pop() || "").toLowerCase();
    return ["docx", "pptx", "pdf"].includes(ext);
  }

  private addReferenceDocumentToSupportingDocs(file: File): void {
    const totalDocs =
      (this.supportingDocumentDocuments?.length || 0) +
      (this.supportingDocumentHyperLinks?.length || 0);
    if (totalDocs >= 10) {
      this.toastr.warning(
        "Supporting documents limit reached (10). Reference document was not added.",
        "Supporting documents"
      );
      return;
    }

    this.flowchestAiImportService.uploadSupportingReferenceDocument(file).subscribe({
      next: (res) => {
        if (!res?.fileName) {
          this.toastr.error("Failed to add reference document to supporting documents.");
          return;
        }
        const documentUrl = environment.documentsStorageBaseUrl + res.fileName;
        const documentName =
          file.name.replace(/\.[^.]+$/, "") || file.name || "Reference document";
        if (!this.supportingDocumentDocuments) {
          this.supportingDocumentDocuments = [];
        }
        this.supportingDocumentDocuments.push({
          documentName,
          documentUrl,
        });
        this.autoSaveCoverPageData();
        this.cdr.detectChanges();
      },
      error: () => {
        this.toastr.error("Failed to add reference document to supporting documents.");
      },
    });
  }

  loadExistingData() {
    const flowCoverPage = JSON.parse(
      localStorage.getItem("flowCoverPageDetails")
    );
    if (flowCoverPage) {
      this.title = flowCoverPage.title || "";
      this.description = flowCoverPage.description || "";
      this.duration = flowCoverPage.duration || 0;
      this.durationUnit = flowCoverPage.durationUnit || 'Mins';
      this.coverImageUrl = flowCoverPage.coverImage || "";
      this.coverPosterUrl = flowCoverPage.coverPosterPageURI || "";
      this.price = flowCoverPage.price || "";
      this.isPaid = flowCoverPage.isPaid || false;
      this.applyCreatorPaidOnlyRules();
      this.searchKeyWords = flowCoverPage.searchKeyWords || "";
      this.supportingDocumentDocuments = flowCoverPage.supportingDocumentDocuments || [];
      this.supportingDocumentHyperLinks = flowCoverPage.supportingDocumentHyperLinks || [];
      this.coverIsSafetyCritical = flowCoverPage.coverIsSafetyCritical || false;
      this.coverSafetyInstructionsTitle = flowCoverPage.coverSafetyInstructionsTitle || "";
      this.coverSafetyInstructionsDescription = flowCoverPage.coverSafetyInstructionsDescription || "";
      if (this.coverImageUrl) {
        this.isCoverImageOrVideo(this.coverImageUrl);
      }
    }

    const steps = JSON.parse(localStorage.getItem("stepsCoverPageDetails"));
    if (steps && Array.isArray(steps)) {
      this.quickSteps = steps.map((step: any, index: number) => ({
        id: `step-${index}`,
        title: step.title || "",
        description: step.description || "",
        imageUrl: step.url || "",
        posterUrl: step.posterUrl || "",
        fileName: step.fileName || "",
        file: null,
        isUploading: false,
        uploadProgress: 0,
        showImage: step.url && this.isImageUrl(step.url),
        showVideo: step.url && this.isVideoUrl(step.url),
        taggedItems: step.taggedItems || [], // Load tagged items
        isSafetyCritical: step.isSafetyCritical || false, // Load safety critical flag
        safetyInstructionsTitle: step.safetyInstructionsTitle || "", // Load safety instructions title
        safetyInstructionsDescription: step.safetyInstructionsDescription || "" // Load safety instructions description
      }));
    }

    this.inames = JSON.parse(localStorage.getItem("ItemsRequired")) || [];
    this.isItemsRequired = this.inames.length > 0;
    this.groupDataByGroupName();
    this.getAllItemGroupName();

    // Reset FAQs on first load - don't load from localStorage
    this._allFaqs = [];
    this.getAllFaqGroupName();
  }

  @HostListener("window:resize", ["$event"])
  onResize(event: any) {
    this.checkMobileView();
  }

  checkMobileView() {
    this.isMobileView = window.innerWidth <= 768;
    this.cdr.detectChanges();
  }

  // Upload all files at once - first file is cover, rest are steps
  async onAllFilesSelect(event: any) {
    if (event.target.files && event.target.files.length > 0) {
      const files = Array.from(event.target.files) as File[];
      
      if (files.length === 0) return;

      // Validate file sizes first
      const maxSize = this.mediaUploadService.maxSourceVideoMb * 1024 * 1024; // 100MB absolute maximum
      const invalidFiles = files.filter(f => f.size > maxSize);
      
      if (invalidFiles.length > 0) {
        const fileNames = invalidFiles.map(f => `${f.name} (${(f.size / 1024 / 1024).toFixed(2)}MB)`).join(', ');
        this.toastr.error(
          `File(s) too large: ${fileNames}. Maximum size is ${this.mediaUploadService.maxSourceVideoMb}MB. Please use smaller files.`,
          'File Size Error',
          { timeOut: 8000 }
        );
        event.target.value = "";
        return;
      }

      this.startUploadSession(files.length);

      try {
        // First file is cover - compress if video
        const coverFile = files[0];
        this.coverIsUploading = true;
        this.coverFile = await this.compressFileIfNeeded(coverFile, (progress) => {
          const fileProgress = Math.floor(progress * 0.5);
          this.coverUploadProgress = fileProgress;
          this.reportUploadStatus('optimizing', 'cover', coverFile, fileProgress, {
            fileIndex: 1,
            fileTotal: files.length,
          });
        }, { target: 'cover', fileIndex: 1, fileTotal: files.length });
        
        // Check if compressed file is still too large
        if (this.coverFile.size > this.mediaUploadService.uploadTargetMb * 1024 * 1024) {
          this.coverIsUploading = false;
          this.toastr.error(
            `File "${this.coverFile.name}" is still too large (${(this.coverFile.size / 1024 / 1024).toFixed(2)}MB) after compression. Maximum upload size is ${this.mediaUploadService.uploadTargetMb}MB after optimization. Please use a smaller or shorter video.`,
            'File Too Large',
            { timeOut: 8000 }
          );
          event.target.value = "";
          return;
        }
        
        this.coverFileName = this.coverFile.name;
        await this.uploadCoverFileAsync(1, files.length);

        // Rest are steps - upload sequentially to avoid overwhelming the server
        if (files.length > 1) {
          const stepFiles = files.slice(1);
          for (let i = 0; i < stepFiles.length; i++) {
            const file = stepFiles[i];
            const stepNumber = i + 1;
            const fileIndex = i + 2;
            // Compress if video
            const compressedFile = await this.compressFileIfNeeded(file, (progress) => {
              const fileProgress = Math.floor(progress * 0.5);
              this.reportUploadStatus('optimizing', 'step', file, fileProgress, {
                stepNumber,
                fileIndex,
                fileTotal: files.length,
              });
            }, { target: 'step', stepNumber, fileIndex, fileTotal: files.length });
            
            // Check if compressed file is still too large
            if (compressedFile.size > this.mediaUploadService.uploadTargetMb * 1024 * 1024) {
              this.toastr.error(
                `File "${compressedFile.name}" is still too large (${(compressedFile.size / 1024 / 1024).toFixed(2)}MB) after compression. Skipping this file.`,
                'File Too Large',
                { timeOut: 6000 }
              );
              continue; // Skip this file
            }
            
            const step: QuickStep = {
              id: `step-${Date.now()}-${Math.random()}`,
              title: "",
              description: "",
              imageUrl: "",
              posterUrl: "",
              fileName: compressedFile.name,
              file: compressedFile,
              originalFile: file,
              isUploading: false,
              uploadProgress: 0,
              showImage: false,
              showVideo: false,
            };
            this.quickSteps.push(step);
            await this.uploadStepFileAsync(step, stepNumber, fileIndex, files.length);
          }
        }
      } catch (error: any) {
        this.coverIsUploading = false;
        this.toastr.error(
          error.message || 'Failed to process files. Please try again with smaller files.',
          'Upload Error',
          { timeOut: 6000 }
        );
      } finally {
        this.endUploadSession();
      }

      // Reset input
      event.target.value = "";
    }
  }

  private async compressFileIfNeeded(
    file: File,
    onProgress?: (progress: number) => void,
    report?: {
      target: 'cover' | 'step' | 'item image';
      stepNumber?: number;
      fileIndex?: number;
      fileTotal?: number;
    }
  ): Promise<File> {
    if (!this.mediaUploadService.isVideoFile(file)) {
      return file;
    }

    const fileName = file.name || 'video';
    const fileSizeMB = this.mediaUploadService.formatMb(file.size);
    if (!this.isMainUploadLoaderActive && !this.uploadSessionActive) {
      this.toastr.info(`Optimizing ${fileName} (${fileSizeMB}MB)...`, "Video Optimization", {
        timeOut: 3000,
        progressBar: true,
      });
    } else if (report) {
      this.reportUploadStatus('optimizing', report.target, file, 0, report);
    }

    const compressed = await this.mediaUploadService.prepareFileForUpload(file, onProgress);
    const compressedSizeMB = this.mediaUploadService.formatMb(compressed.size);

    if (compressed.size < file.size && !this.isMainUploadLoaderActive && !this.uploadSessionActive) {
      const reduction = ((1 - compressed.size / file.size) * 100).toFixed(1);
      this.toastr.success(
        `Optimized to ${compressedSizeMB}MB (${reduction}% smaller)`,
        "Optimization Complete",
        { timeOut: 3000 }
      );
    }

    return compressed;
  }

  // Cover methods
  async onCoverFileSelect(event: any) {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      
      // Validate file size first
      const maxSize = this.mediaUploadService.maxSourceVideoMb * 1024 * 1024; // 100MB absolute maximum
      if (file.size > maxSize) {
        this.toastr.error(
          `File "${file.name}" is too large (${(file.size / 1024 / 1024).toFixed(2)}MB). Maximum size is ${this.mediaUploadService.maxSourceVideoMb}MB. Please use a smaller file.`,
          'File Size Error',
          { timeOut: 8000 }
        );
        event.target.value = "";
        return;
      }

      this.startUploadSession(1);
      
      try {
        // Compress if video
        this.coverFile = await this.compressFileIfNeeded(file, (progress) => {
          const fileProgress = Math.floor(progress * 0.5);
          this.coverUploadProgress = fileProgress;
          this.reportUploadStatus('optimizing', 'cover', file, fileProgress, {
            fileIndex: 1,
            fileTotal: 1,
          });
        }, { target: 'cover', fileIndex: 1, fileTotal: 1 });
        
        // Check if compressed file is still too large
        if (this.coverFile.size > this.mediaUploadService.uploadTargetMb * 1024 * 1024) {
          this.toastr.error(
            `File "${this.coverFile.name}" is still too large (${(this.coverFile.size / 1024 / 1024).toFixed(2)}MB) after compression. Maximum upload size is ${this.mediaUploadService.uploadTargetMb}MB after optimization. Please use a smaller or shorter video.`,
            'File Too Large',
            { timeOut: 8000 }
          );
          event.target.value = "";
          return;
        }
        
        this.coverFileName = this.coverFile.name;
        await this.uploadCoverFileAsync(1, 1);
      } catch (error: any) {
        this.toastr.error(
          error.message || 'Failed to process file. Please try again with a smaller file.',
          'Upload Error',
          { timeOut: 6000 }
        );
        event.target.value = "";
      } finally {
        this.endUploadSession();
      }
    }
  }

  uploadCoverFile() {
    this.uploadCoverFileAsync(1, 1).catch(() => undefined);
  }

  // Async version for sequential uploads
  uploadCoverFileAsync(fileIndex = 1, fileTotal = 1): Promise<void> {
    return new Promise((resolve, reject) => {
      if (!this.coverFile) {
        resolve();
        return;
      }

      const formData = new FormData();
      formData.append("fileData", this.coverFile);

      this.coverIsUploading = true;
      this.coverUploadProgress = 0;

      this.reportUploadStatus('uploading', 'cover', this.coverFile, 50, {
        fileIndex,
        fileTotal,
      });

      const isImage = this.isImageFile(this.coverFile.name);
      const uploadUrl = isImage
        ? environment.apiBaseUrl + environment.uploadImageUrl
        : environment.apiBaseUrl + environment.uploadVideoUrl;

      this.http
        .post(uploadUrl, formData, {
          reportProgress: true,
          observe: "events",
        })
        .subscribe(
          (event: HttpEvent<any>) => {
            switch (event.type) {
              case HttpEventType.UploadProgress:
                if (event.total) {
                  const fileProgress = 50 + Math.round((event.loaded / event.total) * 50);
                  this.coverUploadProgress = fileProgress;
                  this.reportUploadStatus('uploading', 'cover', this.coverFile!, fileProgress, {
                    fileIndex,
                    fileTotal,
                  });
                }
                break;
              case HttpEventType.Response:
                this.handleCoverUpload(event.body, isImage);
                this.finishUploadOperation();
                resolve();
                break;
            }
          },
          (error) => {
            this.coverIsUploading = false;
            this.finishUploadOperation();
            if (error.status === 413) {
              this.toastr.error(
                this.mediaUploadService.getHttp413Message(this.coverFile!.size),
                'Upload Failed - Server Limit',
                { timeOut: 10000 }
              );
            } else {
              this.toastr.error("Cover upload failed", 'Upload Error', { timeOut: 5000 });
            }
            reject(error);
          }
        );
    });
  }

  handleCoverUpload(response: any, isImage: boolean) {
    this.coverIsUploading = false;
    if (response) {
      if (isImage) {
        this.coverImageUrl =
          environment.imageStorageBaseUrl + response.fileName;
        this.showCoverImage = true;
        this.showCoverVideo = false;
      } else {
        this.coverImageUrl =
          environment.videoStorageBaseUrl + response.fileName;
        this.showCoverVideo = true;
        this.showCoverImage = false;
        if (response.posterImageName) {
          this.coverPosterUrl =
            environment.imageStorageBaseUrl + response.posterImageName;
        }
      }
      this.toastr.success("Cover uploaded successfully");
      this.autoSaveCoverPageData();
    }
  }

  removeCover() {
    this.coverImageUrl = "";
    this.coverPosterUrl = "";
    this.coverFile = null;
    this.coverFileName = "";
    this.showCoverImage = false;
    this.showCoverVideo = false;
    this.autoSaveCoverPageData();
  }

  // Steps methods
  async onStepFilesSelect(event: any) {
    if (event.target.files && event.target.files.length > 0) {
      const files = Array.from(event.target.files) as File[];
      
      // Validate file sizes first
      const maxSize = this.mediaUploadService.maxSourceVideoMb * 1024 * 1024; // 100MB absolute maximum
      const invalidFiles = files.filter(f => f.size > maxSize);
      
      if (invalidFiles.length > 0) {
        const fileNames = invalidFiles.map(f => `${f.name} (${(f.size / 1024 / 1024).toFixed(2)}MB)`).join(', ');
        this.toastr.error(
          `File(s) too large: ${fileNames}. Maximum size is ${this.mediaUploadService.maxSourceVideoMb}MB. Please use smaller files.`,
          'File Size Error',
          { timeOut: 8000 }
        );
        event.target.value = "";
        return;
      }

      this.startUploadSession(files.length);

      try {
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const stepNumber = this.quickSteps.length + 1;
          const fileIndex = i + 1;
          try {
            const compressedFile = await this.compressFileIfNeeded(file, (progress) => {
              const fileProgress = Math.floor(progress * 0.5);
              this.reportUploadStatus('optimizing', 'step', file, fileProgress, {
                stepNumber,
                fileIndex,
                fileTotal: files.length,
              });
            }, { target: 'step', stepNumber, fileIndex, fileTotal: files.length });
            
            if (compressedFile.size > this.mediaUploadService.uploadTargetMb * 1024 * 1024) {
              this.toastr.error(
                `File "${compressedFile.name}" is still too large (${(compressedFile.size / 1024 / 1024).toFixed(2)}MB) after compression. Skipping this file.`,
                'File Too Large',
                { timeOut: 6000 }
              );
              continue;
            }
            
            const step: QuickStep = {
              id: `step-${Date.now()}-${Math.random()}`,
              title: "",
              description: "",
              imageUrl: "",
              posterUrl: "",
              fileName: compressedFile.name,
              file: compressedFile,
              originalFile: file,
              isUploading: false,
              uploadProgress: 0,
              showImage: false,
              showVideo: false,
            };
            this.quickSteps.push(step);
            await this.uploadStepFileAsync(step, stepNumber, fileIndex, files.length);
          } catch (error: any) {
            this.toastr.error(
              `Failed to process "${file.name}": ${error.message || 'Please try again with a smaller file.'}`,
              'Upload Error',
              { timeOut: 6000 }
            );
          }
        }
      } finally {
        this.endUploadSession();
      }
      
      event.target.value = "";
    }
  }

  uploadStepFile(step: QuickStep) {
    const stepNumber = this.quickSteps.indexOf(step) + 1;
    this.uploadStepFileAsync(step, stepNumber > 0 ? stepNumber : undefined, 1, 1).catch(() => undefined);
  }

  // Async version for sequential uploads
  uploadStepFileAsync(
    step: QuickStep,
    stepNumber?: number,
    fileIndex = 1,
    fileTotal = 1
  ): Promise<void> {
    return new Promise(async (resolve, reject) => {
      if (!step.file) {
        resolve();
        return;
      }

      try {
        const resolvedStepNumber = stepNumber ?? this.quickSteps.indexOf(step) + 1;
        let workingFile = step.file;

        if (!step.originalFile && step.file) {
          step.originalFile = step.file;
        }

        if (!this.uploadSessionActive) {
          workingFile = await this.compressFileIfNeeded(step.file, (progress) => {
            const fileProgress = Math.floor(progress * 0.5);
            step.uploadProgress = fileProgress;
            this.reportUploadStatus('optimizing', 'step', step.file!, fileProgress, {
              stepNumber: resolvedStepNumber,
              fileIndex,
              fileTotal,
            });
          }, { target: 'step', stepNumber: resolvedStepNumber, fileIndex, fileTotal });
          
          if (workingFile.size > this.mediaUploadService.uploadTargetMb * 1024 * 1024) {
            step.isUploading = false;
            this.toastr.error(
              `File "${workingFile.name}" is still too large (${(workingFile.size / 1024 / 1024).toFixed(2)}MB) after compression. Skipping upload.`,
              'File Too Large',
              { timeOut: 6000 }
            );
            const index = this.quickSteps.indexOf(step);
            if (index > -1) {
              this.quickSteps.splice(index, 1);
            }
            resolve();
            return;
          }
          
          step.file = workingFile;
          step.fileName = workingFile.name;
        }

        const formData = new FormData();
        formData.append("fileData", step.file);

        step.isUploading = true;
        step.uploadProgress = 50;

        this.reportUploadStatus('uploading', 'step', step.file, 50, {
          stepNumber: resolvedStepNumber,
          fileIndex,
          fileTotal,
        });

        const isImage = this.isImageFile(step.file.name);
        const uploadUrl = isImage
          ? environment.apiBaseUrl + environment.uploadImageUrl
          : environment.apiBaseUrl + environment.uploadVideoUrl;

        this.http
          .post(uploadUrl, formData, {
            reportProgress: true,
            observe: "events",
          })
          .subscribe(
            (event: HttpEvent<any>) => {
              switch (event.type) {
                case HttpEventType.UploadProgress:
                  if (event.total) {
                    const fileProgress = 50 + Math.round((event.loaded / event.total) * 50);
                    step.uploadProgress = fileProgress;
                    this.reportUploadStatus('uploading', 'step', step.file!, fileProgress, {
                      stepNumber: resolvedStepNumber,
                      fileIndex,
                      fileTotal,
                    });
                  }
                  break;
                case HttpEventType.Response:
                  this.handleStepUpload(step, event.body, isImage);
                  this.finishUploadOperation();
                  resolve();
                  break;
              }
            },
            (error) => {
              step.isUploading = false;
              this.finishUploadOperation();
              if (error.status === 413) {
                this.toastr.error(
                  this.mediaUploadService.getHttp413Message(step.file!.size),
                  'Upload Failed - Server Limit',
                  { timeOut: 10000 }
                );
              } else {
                this.toastr.error(`Failed to upload ${step.fileName}`, 'Upload Error', { timeOut: 5000 });
              }
              reject(error);
            }
          );
      } catch (error: any) {
        step.isUploading = false;
        this.finishUploadOperation();
        this.toastr.error(
          `Failed to process "${step.fileName}": ${error.message || 'File too large'}`,
          'Processing Error',
          { timeOut: 6000 }
        );
        const index = this.quickSteps.indexOf(step);
        if (index > -1) {
          this.quickSteps.splice(index, 1);
        }
        resolve();
      }
    });
  }

  handleStepUpload(step: QuickStep, response: any, isImage: boolean) {
    step.isUploading = false;
    if (response) {
      if (isImage) {
        step.imageUrl = environment.imageStorageBaseUrl + response.fileName;
        step.showImage = true;
        step.showVideo = false;
      } else {
        step.imageUrl = environment.videoStorageBaseUrl + response.fileName;
        step.showVideo = true;
        step.showImage = false;
        if (response.posterImageName) {
          step.posterUrl =
            environment.imageStorageBaseUrl + response.posterImageName;
        }
      }
      this.toastr.success(`${step.fileName} uploaded successfully`);
      this.autoSaveStepsData();
    }
  }

  removeStep(index: number) {
    this.quickSteps.splice(index, 1);
    this.autoSaveStepsData();
  }

  dropStep(event: CdkDragDrop<QuickStep[]>) {
    moveItemInArray(this.quickSteps, event.previousIndex, event.currentIndex);
    this.autoSaveStepsData();
  }

  // Handle drag and drop between cover and steps
  dropCoverOrStep(event: CdkDragDrop<any>) {
    const dragData = event.item.data;
    const isFromCover = event.previousContainer.id === 'cover-drop-list';
    const isToCover = event.container.id === 'cover-drop-list';
    const isFromSteps = event.previousContainer.id === 'steps-drop-list';
    const isToSteps = event.container.id === 'steps-drop-list';

    // If dragging within the same list (steps to steps), just reorder
    if (isFromSteps && isToSteps) {
      moveItemInArray(this.quickSteps, event.previousIndex, event.currentIndex);
      this.autoSaveStepsData();
      return;
    }

    // If dragging from cover to steps
    if (isFromCover && isToSteps) {
      if (this.quickSteps.length > 0) {
        // Move first step to cover position
        const firstStep = this.quickSteps[0];
        this.coverImageUrl = firstStep.imageUrl;
        this.coverPosterUrl = firstStep.posterUrl;
        this.title = firstStep.title || '';
        this.description = firstStep.description || '';
        this.isCoverImageOrVideo(this.coverImageUrl);
        
        // Remove first step and add cover as first step
        this.quickSteps.splice(0, 1);
        this.quickSteps.unshift({
          id: Date.now().toString(),
          title: dragData.title || '',
          description: dragData.description || '',
          imageUrl: dragData.url,
          posterUrl: dragData.posterUrl || '',
          fileName: '',
          file: null,
          isUploading: false,
          uploadProgress: 0,
          showImage: dragData.showImage,
          showVideo: dragData.showVideo
        });
      } else {
        // No steps exist, just move cover to steps
        this.quickSteps.push({
          id: Date.now().toString(),
          title: dragData.title || '',
          description: dragData.description || '',
          imageUrl: dragData.url,
          posterUrl: dragData.posterUrl || '',
          fileName: '',
          file: null,
          isUploading: false,
          uploadProgress: 0,
          showImage: dragData.showImage,
          showVideo: dragData.showVideo
        });
        this.removeCover();
      }
      this.autoSaveCoverPageData();
      this.autoSaveStepsData();
      return;
    }

    // If dragging from steps to cover
    if (isFromSteps && isToCover) {
      const stepData = dragData.step;
      const stepIndex = event.previousIndex;
      
      // Save current cover to steps if it exists (insert at the position of the step being moved)
      if (this.coverImageUrl) {
        const oldCover: QuickStep = {
          id: Date.now().toString(),
          title: this.title || '',
          description: this.description || '',
          imageUrl: this.coverImageUrl,
          posterUrl: this.coverPosterUrl || '',
          fileName: '',
          file: null,
          isUploading: false,
          uploadProgress: 0,
          showImage: this.showCoverImage,
          showVideo: this.showCoverVideo
        };
        
        // Remove the step first, then insert old cover at that position
        this.quickSteps.splice(stepIndex, 1, oldCover);
      } else {
        // No existing cover, just remove the step
        this.quickSteps.splice(stepIndex, 1);
      }
      
      // Move step to cover
      this.coverImageUrl = stepData.imageUrl;
      this.coverPosterUrl = stepData.posterUrl || '';
      this.title = stepData.title || '';
      this.description = stepData.description || '';
      this.isCoverImageOrVideo(this.coverImageUrl);
      
      this.autoSaveCoverPageData();
      this.autoSaveStepsData();
      return;
    }
  }

  // Items methods - Upload multiple images
  async selectItemFiles(event: any) {
    if (event.target.files && event.target.files.length > 0) {
      const files = Array.from(event.target.files) as File[];
      this.startUploadSession(files.length);

      try {
        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const pendingImage: PendingItemImage = {
            id: `pending-${Date.now()}-${Math.random()}`,
            imageUrl: "",
            fileName: file.name,
            isUploading: true,
            uploadProgress: 0,
            itemName: "",
            itemQuantity: "",
            itemGroupName: "",
            itemWhereToFindIt: "",
          };
          
          this.pendingItemImages.push(pendingImage);
          await this.uploadItemImageAsync(pendingImage, file, i + 1, files.length);
        }
      } finally {
        this.endUploadSession();
      }

      event.target.value = "";
      this.isItemsRequired = true;
    }
  }

  // Legacy single file upload for legacy form
  selectLegacyItemFile(event: any) {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      this.itemUploadProgress = 0;
      this.startUploadSession(1);
      
      const formData = new FormData();
      formData.append("fileData", file);

      this.reportUploadStatus('uploading', 'item image', file, 0, {
        fileIndex: 1,
        fileTotal: 1,
      });

      const uploadUrl = environment.apiBaseUrl + environment.uploadImageUrl;

      this.http
        .post(uploadUrl, formData, {
          reportProgress: true,
          observe: "events",
        })
        .subscribe(
          (event: HttpEvent<any>) => {
            switch (event.type) {
              case HttpEventType.UploadProgress:
                if (event.total) {
                  this.itemUploadProgress = Math.round(
                    (event.loaded / event.total) * 100
                  );
                  this.reportUploadStatus('uploading', 'item image', file, this.itemUploadProgress, {
                    fileIndex: 1,
                    fileTotal: 1,
                  });
                }
                break;
              case HttpEventType.Response:
                const mediaUploadStatus = event.body;
                if (mediaUploadStatus) {
                  this.itemImageUrl =
                    environment.imageStorageBaseUrl +
                    mediaUploadStatus.fileName;
                  this.showItemImage = true;
                  this.itemUploadProgress = 100;
                  this.endUploadSession();
                  this.toastr.success(`${file.name} uploaded successfully`);
                }
                break;
            }
          },
          (error) => {
            this.itemUploadProgress = 0;
            this.endUploadSession();
            this.toastr.error(`Failed to upload ${file.name}`);
          }
        );

      event.target.value = "";
    }
  }

  // Async version for sequential item image uploads
  uploadItemImageAsync(
    pendingImage: PendingItemImage,
    file: File,
    fileIndex = 1,
    fileTotal = 1
  ): Promise<void> {
    return new Promise((resolve, reject) => {
      const formData = new FormData();
      formData.append("fileData", file);

      this.reportUploadStatus('uploading', 'item image', file, 0, {
        fileIndex,
        fileTotal,
      });

      const uploadUrl = environment.apiBaseUrl + environment.uploadImageUrl;

      this.http
        .post(uploadUrl, formData, {
          reportProgress: true,
          observe: "events",
        })
        .subscribe(
          (event: HttpEvent<any>) => {
            switch (event.type) {
              case HttpEventType.UploadProgress:
                if (event.total) {
                  pendingImage.uploadProgress = Math.round(
                    (event.loaded / event.total) * 100
                  );
                  this.reportUploadStatus('uploading', 'item image', file, pendingImage.uploadProgress, {
                    fileIndex,
                    fileTotal,
                  });
                }
                break;
              case HttpEventType.Response:
                const mediaUploadStatus = event.body;
                if (mediaUploadStatus) {
                  pendingImage.imageUrl =
                    environment.imageStorageBaseUrl +
                    mediaUploadStatus.fileName;
                  pendingImage.isUploading = false;
                  pendingImage.uploadProgress = 100;
                  this.finishUploadOperation();
                  this.toastr.success(`${pendingImage.fileName} uploaded successfully`);
                  resolve();
                }
                break;
            }
          },
          (error) => {
            pendingImage.isUploading = false;
            pendingImage.uploadProgress = 0;
            this.finishUploadOperation();
            this.toastr.error(`Failed to upload ${pendingImage.fileName}`);
            const index = this.pendingItemImages.indexOf(pendingImage);
            if (index > -1) {
              this.pendingItemImages.splice(index, 1);
            }
            reject(error);
          }
        );
    });
  }

  // Open item details dialog
  openItemDetailsDialog(item: any) {
    // Handle both PendingItemImage and tagged items
    if (item.isUploading) return;

    const dialogRef = this.dialog.open(ItemDetailsDialogComponent, {
      ...this.getStackedDialogConfig("600px"),
      data: {
        itemName: item.itemName || item.itemkey || '',
        itemQuantity: item.itemQuantity || item.itemValue || item.quantity || '',
        itemGroupName: item.itemGroupName || item.groupName || '',
        itemWhereToFindIt: item.itemWhereToFindIt || item.wheretofindit || item.link || '',
        imageUrl: item.imageUrl || item.url || '',
        itemGroupList: this.itemGroupList,
        readOnly: true // Make it read-only for viewing
      }
    });

    // Only handle result if it's a PendingItemImage (has addItemFromPending method)
    if (item.id && item.isUploading !== undefined) {
      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.addItemFromPending(item, result);
        }
      });
    }
  }

  // Convert pending image to item
  addItemFromPending(pendingImage: PendingItemImage, details?: any) {
    const itemName = details?.itemName || pendingImage.itemName;
    
    if (!itemName) {
      this.toastr.warning("Item name is required");
      return;
    }

    const newItem = {
      itemkey: itemName,
      itemValue: details?.itemQuantity || pendingImage.itemQuantity || "",
      url: pendingImage.imageUrl || "",
      wheretofindit: details?.itemWhereToFindIt || pendingImage.itemWhereToFindIt || "",
      groupName: details?.itemGroupName || pendingImage.itemGroupName || "",
    };

    if (!this.inames) {
      this.inames = [];
    }
    this.inames.push(newItem);
    this.isItemsRequired = true;

    localStorage.setItem("ItemsRequired", JSON.stringify(this.inames));
    this.groupDataByGroupName();
    this.getAllItemGroupName();

    // Remove from pending list
    const index = this.pendingItemImages.indexOf(pendingImage);
    if (index > -1) {
      this.pendingItemImages.splice(index, 1);
    }

    this.toastr.success("Item added successfully");
  }

  // Remove pending image
  removePendingImage(pendingImage: PendingItemImage) {
    const index = this.pendingItemImages.indexOf(pendingImage);
    if (index > -1) {
      this.pendingItemImages.splice(index, 1);
    }
  }

  addItem() {
    if (!this.itemName) {
      this.toastr.warning("Item name is required");
      return;
    }

    const newItem = {
      itemkey: this.itemName,
      itemValue: this.itemQuantity || "",
      url: this.itemImageUrl || "",
      wheretofindit: this.itemWhereToFindIt || "",
      groupName: this.itemGroupName || "",
    };

    // Items added from quick-create-flow or create-flow pages are not saved to library table
    // They are only added to the local flow items list

    if (!this.inames) {
      this.inames = [];
    }
    this.inames.push(newItem);
    this.isItemsRequired = true;

    localStorage.setItem("ItemsRequired", JSON.stringify(this.inames));
    this.groupDataByGroupName();
    this.getAllItemGroupName();

    // Reset form
    this.itemName = "";
    this.itemQuantity = "";
    this.itemImageUrl = "";
    this.itemWhereToFindIt = "";
    this.itemGroupName = "";
    this.showItemImage = false;
    this.itemUploadProgress = 0;
    this.selectedLibraryItem = null;

    this.toastr.success("Item added successfully");
  }

  removeItem(index: number) {
    this.inames.splice(index, 1);
    localStorage.setItem("ItemsRequired", JSON.stringify(this.inames));
    this.groupDataByGroupName();
    this.getAllItemGroupName();
    if (this.inames.length === 0) {
      this.isItemsRequired = false;
    }
  }

  groupDataByGroupName() {
    this.groupedData = {};
    if (this.inames) {
      this.inames.forEach((item) => {
        const group = item.groupName || "Ungrouped";
        if (!this.groupedData[group]) {
          this.groupedData[group] = [];
          // Expand groups by default
          if (this.expandedGroups[group] === undefined) {
            this.expandedGroups[group] = true;
          }
        }
        this.groupedData[group].push(item);
      });
    }
  }

  toggleGroup(groupKey: string) {
    this.expandedGroups[groupKey] = !this.expandedGroups[groupKey];
  }

  openMediaPreview(
    mediaUrl: string | null,
    mediaType?: "image" | "video",
    posterUrl?: string
  ): void {
    if (!mediaUrl) return;

    const resolvedType =
      mediaType ?? (this.isVideoUrl(mediaUrl) ? "video" : "image");

    this.dialog.open(MediaPreviewDialogComponent, {
      ...this.getStackedDialogConfig("min(96vw, 1200px)", {
        panelClass: "media-preview-dialog-pane",
        width: "min(96vw, 1200px)",
        maxWidth: "96vw",
        height: "min(92dvh, 900px)",
        maxHeight: "92dvh",
        disableClose: false,
      }),
      data: {
        mediaUrl,
        mediaType: resolvedType,
        posterUrl: posterUrl || undefined,
      },
    });
  }

  openImage(Url: string) {
    localStorage.setItem("itemsRequiredImageToShow", Url);
    this.dialog.open(ShowFlowImageDialogComponent, {});
  }

  removeItemImage() {
    this.itemImageUrl = "";
    this.showItemImage = false;
    this.itemUploadProgress = 0;
    this.selectedLibraryItem = null;
  }

  openLink(iname: any) {
    var url = iname.wheretofindit;
    if (url) {
      if (url.includes("https://")) {
        url = url;
        window.open(url, "_blank");
      } else {
        url = "https://" + url;
        window.open(url, "_blank");
      }
    }
  }

  getAllItemGroupName() {
    const groups = new Set<string>();
    if (this.inames) {
      this.inames.forEach((item) => {
        if (item.groupName) {
          groups.add(item.groupName);
        }
      });
    }
    this.itemGroupList = Array.from(groups);
  }

  setupLibrarySearch() {
    // Search library when group name is typed (server-side search for performance)
    // This avoids loading all 10,000+ items and only searches when user types
    this.groupNameInput$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((term: string) => {
        if (term && term.trim().length >= 2) {
          return this.itemLibraryService.searchItemLibraries({ groupName: term.trim() });
        }
        this.libraryGroupSuggestions = [];
        return of([]);
      }),
      catchError((error) => {
        console.error('Error searching library groups:', error);
        this.libraryGroupSuggestions = [];
        return of([]);
      })
    ).subscribe((results: ItemLibraryModel[]) => {
      // Backend already expands metadataList entries, so use results directly
      // Extract unique group names from results (already expanded from metadataList)
      // Use Set to ensure no duplicates
      const uniqueGroups = [...new Set(results.map(item => item.groupName).filter(g => g))];
      this.libraryGroupSuggestions = uniqueGroups.map(groupName => ({
        groupName: groupName
      } as ItemLibraryModel));
      this.cdr.detectChanges();
    });

    // Search library when item name is typed (server-side search for performance)
    // This avoids loading all 10,000+ items and only searches when user types
    this.itemNameInput$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((term: string) => {
        if (term && term.trim().length >= 2) {
          return this.itemLibraryService.searchItemLibraries({ itemName: term.trim() });
        }
        this.libraryItemSuggestions = [];
        return of([]);
      }),
      catchError((error) => {
        console.error('Error searching library items:', error);
        this.libraryItemSuggestions = [];
        return of([]);
      })
    ).subscribe((results: ItemLibraryModel[]) => {
      // Backend already expands metadataList entries, so use results directly
      // Filter to ensure we only show items with required fields
      // Deduplicate by itemName + groupName + link combination to avoid duplicates
      const seen = new Set<string>();
      this.libraryItemSuggestions = results.filter(item => {
        if (!item.itemName || !item.groupName) return false;
        const key = `${item.itemName}|${item.groupName}|${item.link || ''}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      this.cdr.detectChanges();
    });
  }

  // Setup step item search (for tagging items to steps)
  setupStepItemSearch() {
    this.stepItemInput$.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      switchMap((term: string) => {
        if (term && term.trim().length >= 2) {
          // Get user email for library search
          const userEmail = this.userData?.emailAddress || '';
          
          // Search both library items and user's items
          const librarySearch = this.itemLibraryService.searchItemLibraries({ 
            itemName: term.trim(),
            userEmail: userEmail
          });
          
          // Combine with user's items from inames
          const userItems = of(
            (this.inames || []).filter(item => 
              (item.itemkey || item.itemName || "").toLowerCase().includes(term.toLowerCase().trim())
            )
          );
          
          return librarySearch.pipe(
            switchMap(libraryResults => {
              return userItems.pipe(
                switchMap(userResults => {
                  // Combine and deduplicate
                  const allResults = [...libraryResults, ...userResults];
                  const seen = new Set<string>();
                  const filteredResults = allResults.filter(item => {
                    const itemName = item.itemName || item.itemkey || "";
                    const key = `${itemName}|${item.groupName || ''}|${item.link || item.wheretofindit || ''}`;
                    if (seen.has(key)) return false;
                    seen.add(key);
                    return true;
                  });
                  
                  // Add "Add new item" option if no results found
                  if (filteredResults.length === 0) {
                    return of([{ 
                      isNewItem: true, 
                      itemName: term.trim(),
                      displayText: `Add "${term.trim()}" as new item`
                    }]);
                  }
                  
                  return of(filteredResults);
                })
              );
            })
          );
        }
        this.stepItemSuggestions = [];
        return of([]);
      }),
      catchError((error) => {
        console.error('Error searching items for step:', error);
        // On error, still show "Add new item" option
        if (this.stepItemSearch && this.stepItemSearch.trim().length >= 2) {
          this.stepItemSuggestions = [{ 
            isNewItem: true, 
            itemName: this.stepItemSearch.trim(),
            displayText: `Add "${this.stepItemSearch.trim()}" as new item`
          }];
        } else {
          this.stepItemSuggestions = [];
        }
        return of(this.stepItemSuggestions);
      })
    ).subscribe((results: any[]) => {
      this.stepItemSuggestions = results || [];
      this.cdr.detectChanges();
    });
  }

  onStepItemSearch(value: string) {
    this.stepItemSearch = value;
    if (value && value.trim().length >= 2) {
      this.stepItemInput$.next(value);
    } else {
      this.stepItemSuggestions = [];
      this.cdr.detectChanges();
    }
  }

  tagItemToStep(item: any) {
    if (!item) return;
    
    // Handle "Add new item" option
    if (item.isNewItem) {
      this.openAddNewItemDialog(item.itemName);
      return;
    }
    
    // Get current step
    if (this.selectedStepIndex < 0 || this.selectedStepIndex >= this.quickSteps.length) {
      this.toastr.warning('Please select a step first');
      return;
    }
    
    const step = this.quickSteps[this.selectedStepIndex];
    if (!step.taggedItems) {
      step.taggedItems = [];
    }
    
    // Check if item is already tagged
    const itemName = item.itemName || item.itemkey || "";
    const isAlreadyTagged = step.taggedItems.some((tagged: any) => 
      (tagged.itemName || tagged.itemkey) === itemName
    );
    
    if (!isAlreadyTagged) {
      // Add item to tagged items with normalized structure
      const taggedItem: any = {
        itemName: item.itemName || item.itemkey || item.itemKey || "",
        itemKey: item.itemkey || item.itemKey || item.itemName || "",
        groupName: item.groupName || "",
        link: item.link || item.wheretofindit || item.whereToFindIt || "",
        imageUrl: item.imageUrl || item.url || "",
        itemValue: item.itemValue || item.quantity || ""
      };
      
      // Remove empty properties to keep structure clean
      Object.keys(taggedItem).forEach((key: string) => {
        const value = (taggedItem as any)[key];
        if (value === "" || value === null || value === undefined) {
          delete (taggedItem as any)[key];
        }
      });
      
      step.taggedItems.push(taggedItem);
      this.currentStepTaggedItems = step.taggedItems;
      this.stepItemSearch = "";
      this.stepItemSuggestions = [];
      this.autoSaveStepsData();
      this.cdr.detectChanges();
    } else {
      this.toastr.info("Item is already tagged to this step");
    }
  }

  removeTaggedItemFromStep(itemIndex: number) {
    if (this.selectedStepIndex < 0 || this.selectedStepIndex >= this.quickSteps.length) {
      return;
    }
    
    const step = this.quickSteps[this.selectedStepIndex];
    if (step.taggedItems && step.taggedItems[itemIndex] !== undefined) {
      step.taggedItems.splice(itemIndex, 1);
      this.currentStepTaggedItems = step.taggedItems;
      this.autoSaveStepsData();
      this.cdr.detectChanges();
    }
  }

  editTaggedItem(item: any, itemIndex: number, event?: Event) {
    if (event) {
      event.stopPropagation();
    }

    if (this.selectedStepIndex < 0 || this.selectedStepIndex >= this.quickSteps.length) {
      return;
    }

    const dialogRef = this.dialog.open(ItemDetailsDialogComponent, {
      ...this.getStackedDialogConfig("600px"),
      data: {
        itemName: item.itemName || item.itemkey || item.itemKey || '',
        itemQuantity: item.itemValue || item.quantity || '',
        itemGroupName: item.groupName || '',
        itemWhereToFindIt: item.wheretofindit || item.link || '',
        imageUrl: item.url || item.imageUrl || '',
        itemGroupList: this.itemGroupList,
        readOnly: false
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const updatedItem: Record<string, any> = {
          ...item,
          itemName: result.itemName || '',
          itemkey: result.itemName || '',
          itemKey: result.itemName || '',
          itemValue: result.itemQuantity || '',
          quantity: result.itemQuantity || '',
          groupName: result.itemGroupName || '',
          link: result.itemWhereToFindIt || '',
          wheretofindit: result.itemWhereToFindIt || ''
        };

        Object.keys(updatedItem).forEach((k: string) => {
          const val = updatedItem[k];
          if (val === '' || val === null || val === undefined) {
            delete updatedItem[k];
          }
        });

        const step = this.quickSteps[this.selectedStepIndex];
        if (step.taggedItems && step.taggedItems[itemIndex] !== undefined) {
          step.taggedItems[itemIndex] = updatedItem;
          this.currentStepTaggedItems = step.taggedItems;
          this.autoSaveStepsData();
        }

        this.cdr.detectChanges();
        this.toastr.success('Tagged item updated successfully');
      }
    });
  }

  openAddNewItemDialog(itemName: string) {
    const dialogRef = this.dialog.open(AddNewItemDialogComponent, {
      width: '600px',
      maxWidth: '90vw',
      data: {
        itemName: itemName || '',
        itemGroupList: this.itemGroupList,
        userData: this.userData
      }
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        const newItem = {
          itemName: result.itemName || '',
          itemkey: result.itemName || '',
          itemKey: result.itemName || '',
          itemValue: result.itemQuantity || '',
          quantity: result.itemQuantity || '',
          groupName: result.itemGroupName || '',
          link: result.itemWhereToFindIt || '',
          wheretofindit: result.itemWhereToFindIt || '',
          imageUrl: result.imageUrl || '',
          url: result.imageUrl || ''
        };

        Object.keys(newItem).forEach(key => {
          if (newItem[key] === '' || newItem[key] === null || newItem[key] === undefined) {
            delete newItem[key];
          }
        });

        // Save to items list if requested
        if (result.saveToItemsList) {
          if (!this.inames) {
            this.inames = [];
          }
          this.inames.push({
            itemkey: newItem.itemName,
            itemValue: newItem.itemValue || '',
            url: newItem.imageUrl || '',
            wheretofindit: newItem.link || '',
            groupName: newItem.groupName || ''
          });
          localStorage.setItem("ItemsRequired", JSON.stringify(this.inames));
          this.groupDataByGroupName();
          this.getAllItemGroupName();
        }

        // Save to library if requested
        if (result.saveToLibrary && this.userData) {
          const libraryItem: ItemLibraryModel = {
            itemName: newItem.itemName,
            groupName: newItem.groupName || '',
            link: newItem.link || '',
            imageUrl: newItem.imageUrl || '',
            isPublic: false,
            createdBy: this.userData.emailAddress
          };

          this.itemLibraryService.createItemLibrary(libraryItem).subscribe(
            (savedItem) => {
              this.toastr.success('Item saved to library successfully');
            },
            (error) => {
              console.error('Error saving item to library:', error);
              this.toastr.error('Failed to save item to library');
            }
          );
        }

        // Add to tagged items
        if (this.selectedStepIndex >= 0 && this.selectedStepIndex < this.quickSteps.length) {
          const step = this.quickSteps[this.selectedStepIndex];
          if (!step.taggedItems) {
            step.taggedItems = [];
          }
          step.taggedItems.push(newItem);
          this.currentStepTaggedItems = step.taggedItems;
          this.autoSaveStepsData();
        }

        this.stepItemSearch = "";
        this.stepItemSuggestions = [];
        this.cdr.detectChanges();
        
        this.toastr.success('Item added successfully');
      }
    });
  }

  onGroupNameInput(value: string) {
    this.itemGroupName = value;
    // Only trigger search if user has typed at least 2 characters (optimized for large datasets)
    if (value && value.trim().length >= 2) {
      this.groupNameInput$.next(value);
    } else {
      // Clear suggestions if less than 2 characters
      this.libraryGroupSuggestions = [];
      this.cdr.detectChanges();
    }
  }

  onItemNameInput(value: string) {
    this.itemName = value;
    // Only trigger search if user has typed at least 2 characters (optimized for large datasets)
    if (value && value.trim().length >= 2) {
      this.itemNameInput$.next(value);
    } else {
      // Clear suggestions if less than 2 characters
      this.libraryItemSuggestions = [];
      this.cdr.detectChanges();
    }
  }

  displayGroupName(group: string): string {
    return group || '';
  }

  displayItemName(item: any): string {
    if (!item) return '';
    if (typeof item === 'string') return item;
    if (item.isNewItem) return item.displayText || item.itemName || '';
    return item.itemName || item.itemkey || '';
  }

  /** Map UI item rows to API FlowComponentsModel shape (itemkey, itemValue, no string id). */
  protected mapComponentsForApi(components: any[]): any[] {
    if (!components?.length) {
      return [];
    }

    return components
      .map((item) => ({
        itemkey: (item.itemkey || item.itemName || item.itemKey || "").trim(),
        itemValue: (item.itemValue || item.itemQuantity || "").trim(),
        url: item.url || item.itemImageUrl || "",
        wheretofindit: item.wheretofindit || item.itemWhereToFindIt || "",
        groupName: item.groupName || item.itemGroupName || "",
      }))
      .filter((item) => item.itemkey.length > 0);
  }

  selectLibraryItem(item: ItemLibraryModel | null) {
    if (item) {
      this.selectedLibraryItem = item;
      this.itemGroupName = item.groupName || "";
      this.itemName = item.itemName || "";
      this.itemWhereToFindIt = item.link || "";
      this.itemImageUrl = item.imageUrl || "";
      if (item.imageUrl) {
        this.showItemImage = true;
      }
    }
  }

  onSelectItemGroup(event: MatAutocompleteSelectedEvent) {
    const selectedValue = event.option.value;
    if (typeof selectedValue === 'string') {
      this.itemGroupName = selectedValue;
    }
    
    // Check if it's from library
    const libraryItem = this.libraryGroupSuggestions.find(
      item => item.groupName === selectedValue
    );
    if (libraryItem) {
      // Optionally populate item name if there's only one item in this group
    }
  }

  // FAQ methods
  addFaqGroup() {
    if (!this.faqGroupName || !this.faqQuestion || !this.faqAnswer) {
      this.toastr.warning("Please fill in all fields");
      return;
    }

    const newFaq = {
      groupName: this.faqGroupName,
      question: this.faqQuestion,
      answer: this.faqAnswer,
      partitionKey: "",
      userEmail: this.userData?.emailAddress,
    };

    if (!this._allFaqs) {
      this._allFaqs = [];
    }
    this._allFaqs.push(newFaq);

    localStorage.setItem("SupportFaqs", JSON.stringify(this._allFaqs));
    this.getAllFaqGroupName();

    // Reset form
    this.faqGroupName = "";
    this.faqQuestion = "";
    this.faqAnswer = "";

    this.toastr.success("FAQ added successfully");
  }

  removeFaq(index: number) {
    this._allFaqs.splice(index, 1);
    localStorage.setItem("SupportFaqs", JSON.stringify(this._allFaqs));
    this.getAllFaqGroupName();
  }

  getAllFaqGroupName() {
    if (!this._allFaqs || this._allFaqs.length === 0) {
      this.faqGroupList = [];
      return;
    }
    const groups = new Set<string>();
    this._allFaqs.forEach((faq) => {
      if (faq.groupName) {
        groups.add(faq.groupName);
      }
    });
    this.faqGroupList = Array.from(groups);
  }

  onSelectFaqGroup(event: any) {
    this.faqGroupName = event.option.value;
  }

  // Auto-save methods
  autoSaveCoverPageData() {
    const coverData = {
      title: this.title,
      description: this.description,
      duration: this.duration,
      durationUnit: this.durationUnit || 'Mins',
      coverImage: this.coverImageUrl,
      coverPosterPageURI: this.coverPosterUrl,
      fileName: this.coverFileName,
      price: this.price,
      isPaid: this.isPaid,
      searchKeyWords: this.searchKeyWords,
      type: this.CategoryName,
      supportingDocumentDocuments: this.supportingDocumentDocuments,
      supportingDocumentHyperLinks: this.supportingDocumentHyperLinks,
    };
    localStorage.setItem("flowCoverPageDetails", JSON.stringify(coverData));
  }

  autoSaveStepsData() {
    const stepsData = this.quickSteps.map((step) => ({
      title: step.title,
      description: step.description,
      url: step.imageUrl,
      posterUrl: step.posterUrl,
      fileName: step.fileName,
      taggedItems: step.taggedItems || [],
      isSafetyCritical: step.isSafetyCritical || false,
      safetyInstructionsTitle: step.safetyInstructionsTitle || "",
      safetyInstructionsDescription: step.safetyInstructionsDescription || ""
    }));
    localStorage.setItem("stepsCoverPageDetails", JSON.stringify(stepsData));
  }

  // Tab Management
  setActiveTab(tabId: 'upload' | 'review') {
    this.activeTab = tabId;
    if (tabId === 'review') {
      this.syncAllStepMediaFlags();
      setTimeout(() => this.refreshActiveSegmentVideo(), 100);
    }
    this.cdr.detectChanges();
  }

  isTabComplete(tabId: string): boolean {
    switch (tabId) {
      case 'upload':
        // Upload tab is complete if cover and at least one step exist
        return !!(this.coverImageUrl && this.quickSteps.length > 0);
      case 'review':
        // Review tab is complete if we have data to review
        return !!(this.coverImageUrl && this.quickSteps.length > 0);
      default:
        return false;
    }
  }

  canPublish(): boolean {
    return !!(this.title && this.coverImageUrl && this.quickSteps.length > 0);
  }

  // Navigation
  private navigateBackFromFlow(): void {
    this.aiPollSub?.unsubscribe();
    this.flowNavigationService.navigateBack(this.returnUrl);
  }

  closeToHome() {
    this.navigateBackFromFlow();
  }

  cancelToSelection() {
    this.navigateBackFromFlow();
  }

  continueToManualFlow() {
    // Save all data and navigate to manual create flow
    this.autoSaveCoverPageData();
    this.autoSaveStepsData();
    this.router.navigate(["/create-guide/manual"]);
  }

  /** Saves the flow as a draft and closes the editor. */
  saveDraft(): void {
    if (!this.title || !this.coverImageUrl) {
      this.toastr.warning("Please add title and cover image to save the flow as a draft.");
      return;
    }

    this.saveFlowToServer('Submitted', true);
  }

  /** Saves draft without navigation or form reset — used by Flowchest AI after import. */
  protected autoSaveDraftSilently(): void {
    if (!this.userData?.emailAddress) {
      return;
    }
    const firstStepVideo = this.quickSteps.find(
      (s) => s.showVideo && s.imageUrl
    )?.imageUrl;
    if (!this.coverImageUrl && firstStepVideo) {
      this.coverImageUrl = firstStepVideo;
      this.isCoverImageOrVideo(firstStepVideo);
    }
    const hasCover = !!(this.coverImageUrl || this.coverPosterUrl || firstStepVideo);
    if (!this.title?.trim() || !hasCover) {
      this.toastr.warning(
        "Flowchest AI import is ready to edit. Add a cover image if missing, then use Save Draft."
      );
      return;
    }

    const steps = this.quickSteps.map((step, index) => ({
      Title: step.title || "",
      Description: step.description || "",
      Url: step.imageUrl || "",
      VideoThumbNailUrl: step.posterUrl || "",
      StepOrder: index + 1,
      TaggedItems: step.taggedItems || [],
      IsSafetyCritical: step.isSafetyCritical || false,
      SafetyInstructionsTitle: step.safetyInstructionsTitle || "",
      SafetyInstructionsDescription: step.safetyInstructionsDescription || "",
    }));

    const flowModel: any = {
      PartitionKey: this.userData.emailAddress,
      RowKey: this.flowRowKey || undefined,
      UniqueId: this.flowUniqueId || "",
      UserName: this.userData.userName || "",
      Description: this.description || "",
      CustomerEmail: this.userData.emailAddress,
      Title: this.title || "",
      AccessType: "Public",
      Url: this.coverImageUrl || "",
      VideoThumbNailUrl: this.coverPosterUrl || "",
      Type: "Flow",
      FlowStatus: "Submitted",
      Duration: this.duration || 0,
      DurationUnit: this.durationUnit || "Mins",
      CoverIsSafetyCritical: this.coverIsSafetyCritical || false,
      CoverSafetyInstructionsTitle: this.coverSafetyInstructionsTitle || "",
      CoverSafetyInstructionsDescription: this.coverSafetyInstructionsDescription || "",
      Steps: steps,
      Components: this.mapComponentsForApi(this.inames || []),
      Price: this.price ? parseFloat(this.price) : 0,
      IsPaid: this.isPaid || false,
      FlowSupportingDocumentsHyperLinkModels: (this.supportingDocumentHyperLinks || []).map((link) => ({
        LinkName: link.linkName || "",
        LinkUrl: link.linkUrl || "",
      })),
      FlowSupportingDocumentsDocumentModels: (this.supportingDocumentDocuments || []).map((doc) => ({
        DocumentName: doc.documentName || "",
        DocumentUrl: doc.documentUrl || "",
      })),
      IsConsulatationAvailable: this.userData.enableConsultation || false,
      ViewerFeatures: this.buildViewerFeaturesPayload(),
      SearchKeyWords: Array.isArray(this.searchKeyWords)
        ? this.searchKeyWords.join(", ")
        : this.searchKeyWords || "",
    };

    this.flowServiceService
      .publishUserFlow(flowModel, this.userData.emailAddress)
      .subscribe({
        next: (event: HttpEvent<any>) => {
          if (event.type !== HttpEventType.Response) {
            return;
          }
          const body = event.body || {};
          this.flowRowKey = body.rowKey || body.RowKey || this.flowRowKey;
          this.flowUniqueId = body.uniqueId || body.UniqueId || this.flowUniqueId;
          localStorage.setItem("draftTabIndex", "2");
          this.toastr.success(
            this.isAiCreateMode
              ? "Your AI flow was saved as a draft."
              : "Your AI flow was saved as a draft. You can keep editing or publish when ready.",
            "Flowchest AI"
          );
          this.activeTab = "review";
          this.cdr.detectChanges();
          // Keep modal open after AI import - user can continue editing or publish manually
        },
        error: (err) => {
          console.error("Auto-save draft failed:", err);
          this.toastr.warning(
            "Import finished but auto-save failed. Use Save Draft before leaving this page.",
            "Flowchest AI",
            { timeOut: 10000 }
          );
        },
      });
  }

  // Publish Flow
  publishFlow(accessType: string) {
    if (this.requiresOrgApproval) {
      this.toastr.info("Save a draft and submit it for approval to publish in your organization.");
      return;
    }
    if (!this.canPublish()) {
      this.toastr.warning("Please add cover page and steps to publish the flow.");
      return;
    }

    if (
      this.creatorRequiresPaidCreation &&
      String(accessType || "").toLowerCase() !== "public"
    ) {
      openCreatorPlanUpgradeDialog(this.dialog, this.router.url);
      return;
    }

    // Validate safety critical cover
    if (this.coverIsSafetyCritical) {
      if (!this.coverSafetyInstructionsTitle || this.coverSafetyInstructionsTitle.trim().length === 0) {
        this.toastr.warning("Safety instructions title is required for safety critical cover.");
        return;
      }
      if (!this.coverSafetyInstructionsDescription || this.coverSafetyInstructionsDescription.trim().length === 0) {
        this.toastr.warning("Safety instructions description is required for safety critical cover.");
        return;
      }
    }

    // Validate safety critical steps
    for (let i = 0; i < this.quickSteps.length; i++) {
      const step = this.quickSteps[i];
      if (step.isSafetyCritical) {
        if (!step.safetyInstructionsTitle || step.safetyInstructionsTitle.trim().length === 0) {
          this.toastr.warning(`Step ${i + 1}: Safety instructions title is required for safety critical steps.`);
          return;
        }
        if (!step.safetyInstructionsDescription || step.safetyInstructionsDescription.trim().length === 0) {
          this.toastr.warning(`Step ${i + 1}: Safety instructions description is required for safety critical steps.`);
          return;
        }
      }
    }

    this.saveFlowToServerAccessLevel(accessType);
  }

  openAccessDialog() {
    if (this.requiresOrgApproval) {
      this.toastr.info("Save a draft and submit it for approval to publish in your organization.");
      return;
    }
    if (!this.canPublish()) {
      this.toastr.warning("Please add cover page and steps to publish the flow.");
      return;
    }

    if (this.coverIsSafetyCritical) {
      if (!this.coverSafetyInstructionsTitle || this.coverSafetyInstructionsTitle.trim().length === 0) {
        this.toastr.warning("Safety instructions title is required for safety critical cover.");
        return;
      }
      if (!this.coverSafetyInstructionsDescription || this.coverSafetyInstructionsDescription.trim().length === 0) {
        this.toastr.warning("Safety instructions description is required for safety critical cover.");
        return;
      }
    }

    for (let i = 0; i < this.quickSteps.length; i++) {
      const step = this.quickSteps[i];
      if (step.isSafetyCritical) {
        if (!step.safetyInstructionsTitle || step.safetyInstructionsTitle.trim().length === 0) {
          this.toastr.warning(`Step ${i + 1}: Safety instructions title is required for safety critical steps.`);
          return;
        }
        if (!step.safetyInstructionsDescription || step.safetyInstructionsDescription.trim().length === 0) {
          this.toastr.warning(`Step ${i + 1}: Safety instructions description is required for safety critical steps.`);
          return;
        }
      }
    }

    if (!this.userData || !this.userData.emailAddress) {
      this.toastr.error("User data is missing. Please sign in again.");
      return;
    }

    const flowModel = this.buildPublishFlowModel("");
    localStorage.setItem("flowModel", JSON.stringify(flowModel));
    const dialogRef = this.dialog.open(EnhancedRestrictedAccessComponent, {
      panelClass: "dialoguebox",
      data: { mode: "publish" },
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (!result) {
        return;
      }
      this.toastr.success("Flow successfully published");
      const publishedRowKey =
        result.rowKey || result.RowKey || this.flowRowKey;
      const publishedTitle = result.title || result.Title || this.title;
      this.resetFormValues();
      setTimeout(() => {
        if (publishedRowKey) {
          this.flowNavigationService.navigateToPublishedFlow(
            publishedTitle || "flow",
            publishedRowKey
          );
        } else {
          this.flowNavigationService.navigateBack(this.returnUrl);
        }
      }, 1000);
    });
  }

  // Save Flow to Server (Draft)
  saveFlowToServer(status: string, navigateAfterSave: boolean = true) {
    if (!this.userData || !this.userData.emailAddress) {
      this.toastr.error("User data is missing. Please sign in again.");
      return;
    }

    // Validate safety critical cover
    if (this.coverIsSafetyCritical) {
      if (!this.coverSafetyInstructionsTitle || this.coverSafetyInstructionsTitle.trim().length === 0) {
        this.toastr.warning("Safety instructions title is required for safety critical cover.");
        return;
      }
      if (!this.coverSafetyInstructionsDescription || this.coverSafetyInstructionsDescription.trim().length === 0) {
        this.toastr.warning("Safety instructions description is required for safety critical cover.");
        return;
      }
    }

    // Validate safety critical steps
    for (let i = 0; i < this.quickSteps.length; i++) {
      const step = this.quickSteps[i];
      if (step.isSafetyCritical) {
        if (!step.safetyInstructionsTitle || step.safetyInstructionsTitle.trim().length === 0) {
          this.toastr.warning(`Step ${i + 1}: Safety instructions title is required for safety critical steps.`);
          return;
        }
        if (!step.safetyInstructionsDescription || step.safetyInstructionsDescription.trim().length === 0) {
          this.toastr.warning(`Step ${i + 1}: Safety instructions description is required for safety critical steps.`);
          return;
        }
      }
    }

    // Map quickSteps to match the expected Steps format
    const steps = this.quickSteps.map((step, index) => ({
      Title: step.title || "",
      Description: step.description || "",
      Url: step.imageUrl || "",
      VideoThumbNailUrl: step.posterUrl || "",
      StepOrder: index + 1,
      TaggedItems: step.taggedItems || [], // Include tagged items
      IsSafetyCritical: step.isSafetyCritical || false, // Include safety critical flag
      SafetyInstructionsTitle: step.safetyInstructionsTitle || "", // Include safety instructions title
      SafetyInstructionsDescription: step.safetyInstructionsDescription || "" // Include safety instructions description
    }));

    const flowModel: any = {
      PartitionKey: this.userData.emailAddress,
      RowKey: this.flowRowKey || undefined,
      UniqueId: this.flowUniqueId || "",
      UserName: this.userData.userName || "",
      Description: this.description || "",
      CustomerEmail: this.userData.emailAddress,
      Title: this.title || "",
      AccessType: "Public",
      Url: this.coverImageUrl || "",
      VideoThumbNailUrl: this.coverPosterUrl || "",
      Type: "Flow",
      FlowStatus: status || "Draft",
      Duration: this.duration || 0,
      DurationUnit: this.durationUnit || 'Mins',
      CoverIsSafetyCritical: this.coverIsSafetyCritical || false,
      CoverSafetyInstructionsTitle: this.coverSafetyInstructionsTitle || "",
      CoverSafetyInstructionsDescription: this.coverSafetyInstructionsDescription || "",
      Steps: steps,
      Components: this.mapComponentsForApi(this.inames || []),
      Price: this.price ? parseFloat(this.price) : 0,
      IsPaid: this.isPaid || false,
      FlowSupportingDocumentsHyperLinkModels: (this.supportingDocumentHyperLinks || []).map(link => ({
        LinkName: link.linkName || '',
        LinkUrl: link.linkUrl || ''
      })),
      FlowSupportingDocumentsDocumentModels: (this.supportingDocumentDocuments || []).map(doc => ({
        DocumentName: doc.documentName || '',
        DocumentUrl: doc.documentUrl || ''
      })),
      IsConsulatationAvailable: this.userData.enableConsultation || false,
      ViewerFeatures: this.buildViewerFeaturesPayload(),
      SearchKeyWords: Array.isArray(this.searchKeyWords)
        ? this.searchKeyWords.join(", ")
        : this.searchKeyWords || "",
    };

    console.log("Saving flow model:", flowModel);

    this.flowServiceService
      .publishUserFlow(flowModel, this.userData.emailAddress)
      .subscribe({
        next: (event: HttpEvent<any>) => {
          switch (event.type) {
            case HttpEventType.Sent:
              console.log("Request sent to server.");
              break;
            case HttpEventType.UploadProgress:
              const progress = Math.round((95 * event.loaded) / (event.total || 1));
              console.log(`Progress: ${progress}%`);
              break;
            case HttpEventType.Response:
              console.log("Upload complete:", event.body);
              const draftBody = event.body || {};
              this.flowRowKey = draftBody.rowKey || draftBody.RowKey || this.flowRowKey;
              this.flowUniqueId = draftBody.uniqueId || draftBody.UniqueId || this.flowUniqueId;
              localStorage.setItem("draftTabIndex", "2");
              
              if (navigateAfterSave) {
                // Original behavior: reset and navigate away
                this.toastr.success("Successfully saved as draft");
                this.resetFormValues();
                setTimeout(() => {
                  this.flowNavigationService.navigateBack(this.returnUrl);
                }, 1000);
              } else {
                // Keep editing flow open and switch to review for preview
                this.toastr.success(
                  "Draft saved successfully. You can continue editing or publish when ready.",
                  "Draft Saved",
                  { timeOut: 4000 }
                );
                this.activeTab = "review";
                this.cdr.detectChanges();
              }
              break;
            default:
              console.log("Unhandled event:", event);
          }
        },
        error: (err) => {
          console.error("Error saving:", err);
          
          // Provide more specific error messages
          if (err.status === 0) {
            this.toastr.error("Network error: Unable to connect to server. Please check your connection and try again.");
          } else if (err.status === 502) {
            this.toastr.error("Server error: The server is temporarily unavailable. Please try again later.");
          } else if (isMaxPublishedFlowsError(err)) {
            handleMaxPublishedFlowsError(this.router, this.toastr, err, this.returnUrl || this.router.url);
          } else if (isFlowBundlePermissionError(err)) {
            handleFlowBundlePermissionError(this.toastr, err);
          } else if (isCreatorPublicOnlyError(err)) {
            handleCreatorPublicOnlyError(this.dialog, this.returnUrl || this.router.url, err);
          } else if (err.status === 403 || err.status === 401) {
            this.toastr.error("Authentication error: Please sign in again.");
          } else {
            this.toastr.error(`Failed to save flow: ${err.message || "Please try again."}`);
          }
        },
      });
  }

  private buildPublishFlowModel(accessType: string): any {
    const steps = this.quickSteps.map((step, index) => ({
      Title: step.title || "",
      Description: step.description || "",
      Url: step.imageUrl || "",
      VideoThumbNailUrl: step.posterUrl || "",
      StepOrder: index + 1,
      TaggedItems: step.taggedItems || [],
      IsSafetyCritical: step.isSafetyCritical || false,
      SafetyInstructionsTitle: step.safetyInstructionsTitle || "",
      SafetyInstructionsDescription: step.safetyInstructionsDescription || "",
    }));

    return {
      PartitionKey: this.userData.emailAddress,
      RowKey: this.flowRowKey || undefined,
      UniqueId: this.flowUniqueId || "",
      UserName: this.userData.userName || "",
      Description: this.description || "",
      CustomerEmail: this.userData.emailAddress,
      Title: this.title || "",
      AccessType: accessType || "Public",
      Url: this.coverImageUrl || "",
      VideoThumbNailUrl: this.coverPosterUrl || "",
      Type: "Flow",
      FlowStatus: "Published",
      Duration: this.duration || 0,
      DurationUnit: this.durationUnit || "Mins",
      CoverIsSafetyCritical: this.coverIsSafetyCritical || false,
      CoverSafetyInstructionsTitle: this.coverSafetyInstructionsTitle || "",
      CoverSafetyInstructionsDescription: this.coverSafetyInstructionsDescription || "",
      Steps: steps,
      Components: this.mapComponentsForApi(this.inames || []),
      Price: this.price ? parseFloat(this.price) : 0,
      IsPaid: this.isPaid || false,
      FlowSupportingDocumentsHyperLinkModels: (this.supportingDocumentHyperLinks || []).map((link) => ({
        LinkName: link.linkName || "",
        LinkUrl: link.linkUrl || "",
      })),
      FlowSupportingDocumentsDocumentModels: (this.supportingDocumentDocuments || []).map((doc) => ({
        DocumentName: doc.documentName || "",
        DocumentUrl: doc.documentUrl || "",
      })),
      IsConsulatationAvailable: this.userData.enableConsultation || false,
      ViewerFeatures: this.buildViewerFeaturesPayload(),
      SearchKeyWords: Array.isArray(this.searchKeyWords)
        ? this.searchKeyWords.join(", ")
        : this.searchKeyWords || "",
    };
  }

  private buildViewerFeaturesPayload(): any {
    return {
      ShowShare: !!this.viewerFeatures.showShare,
      ShowComments: !!this.viewerFeatures.showComments,
      ShowLike: !!this.viewerFeatures.showLike,
      ShowReview: !!this.viewerFeatures.showReview,
      ShowSave: !!this.viewerFeatures.showSave,
      ShowQr: !!this.viewerFeatures.showQr,
      ShowReport: !!this.viewerFeatures.showReport,
      ShowFollow: !!this.viewerFeatures.showFollow,
      ShowSchedule: !!this.viewerFeatures.showSchedule,
      ShowConsult: !!this.viewerFeatures.showConsult,
      ShowChatbot: !!this.viewerFeatures.showChatbot,
      ShowCopyLinks: !!this.viewerFeatures.showCopyLinks,
      ShowTransfer: !!this.viewerFeatures.showTransfer,
    };
  }

  // Save Flow to Server with Access Level (Publish)
  saveFlowToServerAccessLevel(accessType: string) {
    if (!this.userData || !this.userData.emailAddress) {
      this.toastr.error("User data is missing. Please sign in again.");
      return;
    }

    if (this.quickSteps.length <= 0) {
      this.toastr.warning("Please add the cover page and steps to publish the flow.");
      return;
    }

    const flowModel: any = this.buildPublishFlowModel(accessType);

    console.log("Publishing flow model:", flowModel);

    this.flowServiceService
      .publishUserFlow(flowModel, this.userData.emailAddress)
      .subscribe({
        next: (event: HttpEvent<any>) => {
          switch (event.type) {
            case HttpEventType.Sent:
              console.log("Request sent to server.");
              break;
            case HttpEventType.UploadProgress:
              const progress = Math.round((95 * event.loaded) / (event.total || 1));
              console.log(`Progress: ${progress}%`);
              break;
            case HttpEventType.Response:
              console.log("Upload complete:", event.body);
              localStorage.setItem("draftTabIndex", "1");
              const publishedBody = event.body || {};
              const publishedRowKey =
                publishedBody.rowKey ||
                publishedBody.RowKey ||
                this.flowRowKey;
              const publishedTitle =
                publishedBody.title || publishedBody.Title || this.title;
              this.toastr.success("Flow successfully published");
              this.resetFormValues();
              setTimeout(() => {
                if (publishedRowKey) {
                  this.flowNavigationService.navigateToPublishedFlow(
                    publishedTitle || "flow",
                    publishedRowKey
                  );
                } else {
                  this.flowNavigationService.navigateBack(this.returnUrl);
                }
              }, 1000);
              break;
            default:
              console.log("Unhandled event:", event);
          }
        },
        error: (err) => {
          console.error("Error publishing:", err);
          
          // Provide more specific error messages
          if (err.status === 0) {
            this.toastr.error("Network error: Unable to connect to server. Please check your connection and try again.");
          } else if (err.status === 502) {
            this.toastr.error("Server error: The server is temporarily unavailable. Please try again later.");
          } else if (isMaxPublishedFlowsError(err)) {
            handleMaxPublishedFlowsError(this.router, this.toastr, err, this.returnUrl || this.router.url);
          } else if (isFlowBundlePermissionError(err)) {
            handleFlowBundlePermissionError(this.toastr, err);
          } else if (isCreatorPublicOnlyError(err)) {
            handleCreatorPublicOnlyError(this.dialog, this.returnUrl || this.router.url, err);
          } else if (err.status === 403 || err.status === 401) {
            this.toastr.error("Authentication error: Please sign in again.");
          } else {
            this.toastr.error(`Failed to publish flow: ${err.message || "Please try again."}`);
          }
        },
      });
  }

  // Utility methods
  isImageFile(fileName: string): boolean {
    const imageExtensions = [
      "jpeg",
      "jpg",
      "png",
      "gif",
      "jfif",
      "tiff",
      "tif",
      "pdf",
      "bmp",
      "webp",
      "svg",
    ];
    const ext = fileName.split(".").pop()?.toLowerCase() || "";
    return imageExtensions.includes(ext);
  }

  isImageUrl(url: string): boolean {
    return url.includes("mediaimage") || url.startsWith("data:image");
  }

  isVideoUrl(url: string): boolean {
    return url.includes("mediavideo") || url.startsWith("data:video");
  }

  isCoverImageOrVideo(url: string) {
    if (this.isImageUrl(url)) {
      this.showCoverImage = true;
      this.showCoverVideo = false;
    } else if (this.isVideoUrl(url)) {
      this.showCoverVideo = true;
      this.showCoverImage = false;
    }
  }

  selectSummaryIndex(index: number): void {
    this.clearSegmentPlayback();
    this.selectedSummaryIndex = index;
    setTimeout(() => this.refreshActiveSegmentVideo(), 50);
  }

  protected getEffectiveStepEndSec(step: QuickStep): number {
    const startSec = step.videoStartSec ?? 0;
    const naturalEnd = step.videoEndSec ?? startSec + 30;
    if (step.useSegmentPlayback && step.limitClipTo30Sec !== false) {
      return Math.min(naturalEnd, startSec + 30);
    }
    return naturalEnd;
  }

  onStepLimitClipToggle(step: QuickStep): void {
    if (!step.useSegmentPlayback) {
      return;
    }
    this.refreshActiveSegmentVideo();
    this.cdr.detectChanges();
  }

  getStepVideoSrc(step: QuickStep | undefined): string {
    if (!step?.imageUrl) {
      return "";
    }

    const baseUrl = step.imageUrl.split("#")[0];
    if (
      step.useSegmentPlayback &&
      step.videoStartSec != null &&
      Number.isFinite(step.videoStartSec)
    ) {
      const endSec = this.getEffectiveStepEndSec(step);
      return `${baseUrl}#t=${step.videoStartSec},${endSec}`;
    }

    return step.imageUrl;
  }

  onSegmentVideoReady(videoEl: HTMLVideoElement, stepIndex: number): void {
    if (stepIndex < 0 || stepIndex >= this.quickSteps.length) {
      return;
    }
    if (this.selectedSummaryIndex === stepIndex) {
      this.setupSegmentPlayback(videoEl, this.quickSteps[stepIndex]);
    }
  }

  protected refreshActiveSegmentVideo(): void {
    this.clearSegmentPlayback();
    if (this.selectedSummaryIndex < 0 || this.selectedSummaryIndex >= this.quickSteps.length) {
      return;
    }
    const videoEl = document.querySelector(
      ".video-player-section .main-video-player"
    ) as HTMLVideoElement | null;
    if (videoEl) {
      this.setupSegmentPlayback(videoEl, this.quickSteps[this.selectedSummaryIndex]);
    }
  }

  private setupSegmentPlayback(videoEl: HTMLVideoElement, step: QuickStep): void {
    this.clearSegmentPlayback();
    if (!step?.useSegmentPlayback || step.videoStartSec == null) {
      return;
    }

    const startSec = step.videoStartSec;
    const endSec = this.getEffectiveStepEndSec(step);

    const seekToStart = () => {
      if (videoEl.currentTime < startSec - 0.05 || videoEl.currentTime > endSec + 0.05) {
        videoEl.currentTime = startSec;
      }
    };

    const onTimeUpdate = () => {
      if (videoEl.currentTime >= endSec - 0.08) {
        videoEl.pause();
        videoEl.currentTime = startSec;
      }
    };

    videoEl.addEventListener("loadedmetadata", seekToStart);
    videoEl.addEventListener("play", seekToStart);
    videoEl.addEventListener("timeupdate", onTimeUpdate);

    if (videoEl.readyState >= 1) {
      seekToStart();
    }

    this.segmentPlaybackCleanup = () => {
      videoEl.removeEventListener("loadedmetadata", seekToStart);
      videoEl.removeEventListener("play", seekToStart);
      videoEl.removeEventListener("timeupdate", onTimeUpdate);
    };
  }

  protected clearSegmentPlayback(): void {
    this.segmentPlaybackCleanup?.();
    this.segmentPlaybackCleanup = null;
  }

  titleChanged() {
    if (this.title) {
      this.title = this.title.trim();
      if (this.title.length > this.maxtitlelenght) {
        this.title = this.title.substring(0, this.maxtitlelenght);
      }
      this.autoSaveCoverPageData();
    }
  }

  checkValue() {
    if (isOrganizationAccountMode()) {
      this.isPaid = false;
    } else if (this.creatorRequiresPaidCreation) {
      this.isPaid = true;
    }
    if (!this.isPaid) {
      this.price = "";
    }
    this.autoSaveCoverPageData();
  }

  /** Organization content is never sold, so the price section is hidden. */
  get showPriceSection(): boolean {
    return !isOrganizationAccountMode();
  }

  private applyCreatorPaidOnlyRules(): void {
    if (isOrganizationAccountMode()) {
      this.creatorRequiresPaidCreation = false;
      this.isPaid = false;
      this.price = "";
      return;
    }
    this.creatorRequiresPaidCreation = isCreatorPlanUser(this.userData);
    if (this.creatorRequiresPaidCreation) {
      this.isPaid = true;
    }
  }

  goToUpgradeForFreeContent(): void {
    this.persistDraftBeforeLeaving();
    this.router.navigate(["/pricing"], {
      queryParams: { upgradeFree: "1", returnUrl: this.router.url },
    });
  }

  private navigateToCoinTopUp(): void {
    this.persistDraftBeforeLeaving();
    setCoinPurchaseReturnUrl(this.router.url);
    this.router.navigate(["/pricing"], {
      queryParams: { buyCoins: "1", returnUrl: this.router.url },
      fragment: "ai-coins",
    });
  }

  /** Saves the in-progress flow to localStorage so it survives the pricing/purchase round trip. */
  private persistDraftBeforeLeaving(): void {
    this.autoSaveCoverPageData();
    this.autoSaveStepsData();
  }

  // Open caption dialog for cover
  openCoverCaptionDialog() {
    const dialogRef = this.dialog.open(CaptionDialogComponent, {
      ...this.getStackedDialogConfig("700px", { panelClass: "caption-dialog-pane" }),
      data: {
        title: this.title,
        description: this.description,
        type: 'cover',
        isVideo: this.showCoverVideo,
        posterUrl: this.coverPosterUrl,
        mediaUrl: this.coverImageUrl,
        supportingDocumentDocuments: this.supportingDocumentDocuments,
        supportingDocumentHyperLinks: this.supportingDocumentHyperLinks,
        isSafetyCritical: this.coverIsSafetyCritical || false,
        safetyInstructionsTitle: this.coverSafetyInstructionsTitle || "",
        safetyInstructionsDescription: this.coverSafetyInstructionsDescription || ""
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.title = result.title;
        this.description = result.description;
        if (result.posterUrl) {
          this.coverPosterUrl = result.posterUrl;
        }
        if (result.supportingDocumentDocuments) {
          this.supportingDocumentDocuments = result.supportingDocumentDocuments;
        }
        if (result.supportingDocumentHyperLinks) {
          this.supportingDocumentHyperLinks = result.supportingDocumentHyperLinks;
        }
        // Update cover safety fields if provided
        if (result.isSafetyCritical !== undefined) {
          this.coverIsSafetyCritical = result.isSafetyCritical;
          this.coverSafetyInstructionsTitle = result.safetyInstructionsTitle || "";
          this.coverSafetyInstructionsDescription = result.safetyInstructionsDescription || "";
        }
        this.autoSaveCoverPageData();
        this.cdr.detectChanges();
      }
    });
  }

  // Select step and update currentStepTaggedItems
  selectStep(stepIndex: number) {
    this.selectedStepIndex = stepIndex;
    if (stepIndex >= 0 && stepIndex < this.quickSteps.length) {
      const step = this.quickSteps[stepIndex];
      this.currentStepTaggedItems = step.taggedItems || [];
    } else {
      this.currentStepTaggedItems = [];
    }
  }

  // Open caption dialog for step
  openStepCaptionDialog(stepIndex: number) {
    if (stepIndex < 0 || stepIndex >= this.quickSteps.length) return;

    const step = this.quickSteps[stepIndex];
    const dialogRef = this.dialog.open(CaptionDialogComponent, {
      ...this.getStackedDialogConfig("600px", { panelClass: "caption-dialog-pane" }),
      data: {
        title: step.title,
        description: step.description,
        type: 'step',
        stepIndex: stepIndex,
        isVideo: step.showVideo,
        posterUrl: step.posterUrl,
        mediaUrl: step.imageUrl,
        taggedItems: step.taggedItems || [],
        itemGroupList: this.itemGroupList,
        userData: this.userData,
        inames: this.inames,
        isSafetyCritical: step.isSafetyCritical || false,
        safetyInstructionsTitle: step.safetyInstructionsTitle || "",
        safetyInstructionsDescription: step.safetyInstructionsDescription || ""
      }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        step.title = result.title;
        step.description = result.description;
        if (result.posterUrl) {
          step.posterUrl = result.posterUrl;
        }
        // Update tagged items if provided
        if (result.taggedItems !== undefined) {
          step.taggedItems = result.taggedItems as any[] || [];
          // Update currentStepTaggedItems if this is the selected step
          if (this.selectedStepIndex === stepIndex) {
            this.currentStepTaggedItems = step.taggedItems;
          }
        }
        // Update safety fields if provided
        if (result.isSafetyCritical !== undefined) {
          step.isSafetyCritical = result.isSafetyCritical;
          step.safetyInstructionsTitle = result.safetyInstructionsTitle || "";
          step.safetyInstructionsDescription = result.safetyInstructionsDescription || "";
        }
        this.autoSaveStepsData();
        this.cdr.detectChanges();
      }
    });
  }

  // Reset form values after successful submission
  // Note: We DON'T clear localStorage so when user comes back, 
  // loadExistingData() will load their previously saved data
  resetFormValues() {
    // Reset cover properties
    this.coverImageUrl = "";
    this.coverPosterUrl = "";
    this.coverFile = null;
    this.coverFileName = "";
    this.coverIsUploading = false;
    this.coverUploadProgress = 0;
    this.showCoverImage = false;
    this.showCoverVideo = false;

    // Reset flow properties
    this.title = "";
    this.description = "";
    this.duration = 0;
    this.durationUnit = 'Mins';
    this.isPaid = false;
    this.price = "";
    this.searchKeyWords = "";

    // Reset steps
    this.quickSteps = [];
    this.selectedStepIndex = -1;

    // Reset items
    this.inames = [];
    this.isItemsRequired = false;
    this.itemGroupName = "";
    this.itemName = "";
    this.itemQuantity = "";
    this.itemImageUrl = "";
    this.itemWhereToFindIt = "";
    this.itemGroupList = [];
    this.showItemImage = false;
    this.itemUploadProgress = 0;
    this.groupedData = {};
    this.expandedGroups = {};

    // Reset FAQs
    this._allFaqs = [];
    this.faqGroupName = "";
    this.faqQuestion = "";
    this.faqAnswer = "";
    this.faqGroupList = [];

    // Reset UI state
    this.activeTab = 'upload';
    this.selectedSummaryIndex = -2;
    this.showDescriptionExpanded = false;

    // Note: We intentionally DON'T clear localStorage here
    // so that when user comes back, loadExistingData() will load old saved data
  }

  // Loading modal methods
  private startUploadSession(totalFiles: number): void {
    this.uploadSessionActive = true;
    this.uploadSessionTotal = totalFiles;
    this.isMainUploadLoaderActive = true;
    const detail = totalFiles > 1 ? `${totalFiles} files queued` : undefined;
    this.openLoadingModal(
      totalFiles > 1 ? `Preparing ${totalFiles} files...` : 'Preparing upload...',
      true,
      detail
    );
  }

  private endUploadSession(): void {
    this.uploadSessionActive = false;
    this.uploadSessionTotal = 0;
    this.isMainUploadLoaderActive = false;
    this.closeLoadingModal();
  }

  private finishUploadOperation(): void {
    if (!this.uploadSessionActive) {
      this.isMainUploadLoaderActive = false;
      this.closeLoadingModal();
    }
  }

  private getUploadMediaTypeLabel(
    file: File,
    target: 'cover' | 'step' | 'item image',
    stepNumber?: number
  ): string {
    if (target === 'item image') {
      return 'item image';
    }
    const fileName = file.name || 'file';
    const isImage = this.isImageFile(fileName);
    if (target === 'cover') {
      return isImage ? 'cover image' : 'cover video';
    }
    const stepLabel = stepNumber ? `step ${stepNumber}` : 'step';
    return isImage ? `${stepLabel} image` : `${stepLabel} video`;
  }

  private computeOverallUploadProgress(
    fileIndex: number | undefined,
    fileTotal: number | undefined,
    fileProgress: number
  ): number {
    const total = (fileTotal && fileTotal > 1) ? fileTotal : 1;
    const index = (fileIndex && fileIndex > 0) ? fileIndex : 1;
    if (total <= 1) {
      return Math.min(100, Math.max(0, Math.round(fileProgress)));
    }
    const completed = index - 1;
    return Math.min(
      100,
      Math.max(0, Math.round(((completed / total) + (fileProgress / 100 / total)) * 100))
    );
  }

  private reportUploadStatus(
    phase: 'preparing' | 'optimizing' | 'uploading',
    target: 'cover' | 'step' | 'item image',
    file: File,
    fileProgress: number,
    options?: { stepNumber?: number; fileIndex?: number; fileTotal?: number }
  ): void {
    const mediaLabel = this.getUploadMediaTypeLabel(file, target, options?.stepNumber);
    const phaseVerb =
      phase === 'preparing' ? 'Preparing' : phase === 'optimizing' ? 'Optimizing' : 'Uploading';
    const message = `${phaseVerb} ${mediaLabel}...`;
    const total = options?.fileTotal ?? this.uploadSessionTotal;
    const index = options?.fileIndex;
    let detail = file.name;
    if (total > 1 && index) {
      detail = `File ${index} of ${total} · ${file.name}`;
    }

    const overallProgress = this.computeOverallUploadProgress(index, total, fileProgress);

    if (!this.loadingModalRef) {
      this.isMainUploadLoaderActive = true;
      this.openLoadingModal(message, true, detail);
    } else {
      this.updateLoadingModal(message, overallProgress);
      this.updateLoadingModalDetail(detail);
    }
  }

  openLoadingModal(message: string, showProgress: boolean = false, detail?: string) {
    if (this.loadingModalRef && this.uploadSessionActive) {
      this.updateLoadingModal(message, 0);
      if (detail !== undefined) {
        this.updateLoadingModalDetail(detail);
      }
      return;
    }
    if (this.loadingModalRef) {
      this.closeLoadingModal();
    }
    this.isMainUploadLoaderActive = true;
    this.loadingModalRef = this.dialog.open(LoadingModalComponent, {
      ...this.getStackedDialogConfig("400px", { disableClose: true }),
      data: {
        message: message,
        detail: detail || "",
        progress: 0,
        showProgress: showProgress,
      } as LoadingModalData,
    });
  }

  updateLoadingModal(message?: string, progress?: number) {
    if (this.loadingModalRef && this.loadingModalRef.componentInstance) {
      if (message) {
        this.loadingModalRef.componentInstance.updateMessage(message);
      }
      if (progress !== undefined) {
        this.loadingModalRef.componentInstance.updateProgress(progress);
      }
    }
  }

  updateLoadingModalDetail(detail: string) {
    if (this.loadingModalRef && this.loadingModalRef.componentInstance) {
      this.loadingModalRef.componentInstance.updateDetail(detail);
    }
  }

  closeLoadingModal() {
    if (this.loadingModalRef) {
      this.loadingModalRef.close();
      this.loadingModalRef = null;
    }
  }
}
